import { useState, useEffect } from "react";
import React from "react";
import { db, storage } from "../firebase";
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import Icon from "../components/Icon";
import ConfirmDelete from "../components/ConfirmDelete";
import UnsavedChangesGuard from "../components/UnsavedChangesGuard";
import CommunicationsTimeline from "../components/CommunicationsTimeline";
import { useMailStatus, MailStatusBadge, sendEstimateEmail } from "../useFirestore";
import { fmt, calcEstimateTotal, countItems, generateReportHTML } from "../utils";
import { JobBuilder } from "./Jobs";

const ESTIMATE_TEMPLATES = [
  { id:1, name:"Excavation", service:"Hardscape", itemCount:9 },
  { id:2, name:"Excavation Piscine (121 t.m.)", service:"", itemCount:9 },
  { id:3, name:"Excavation/Remblais", service:"Hardscape", itemCount:9, desc:"Excavation, rebuts, camion, géo, gravier, compaction, M/O" },
  { id:4, name:"Excavation/Remblais Piscine", service:"", itemCount:31 },
  { id:5, name:"Finition", service:"Landscape Design", itemCount:22 },
  { id:6, name:"Patio Composite", service:"Hardscape", itemCount:24 },
];

function EstimateBadge({ estimateId }) {
  const status = useMailStatus("estimate", estimateId);
  return <MailStatusBadge status={status} />;
}

function Estimates({ t, estimates, addEstimate, updateEstimate, deleteEstimate, customers, priceList, addPriceItem, updatePriceItem, templates, addTemplate, updateTemplate, deleteTemplate, comms, addComm, updateComm, deleteComm, teamMembers, crews, jobs, addJob, onCreateInvoice, settings, scheduleNotes, updateScheduleNote, sectionTemplates, addSectionTemplate, updateSectionTemplate, deleteSectionTemplate, preselectCustomerId, onPreselectConsumed, leads, updateLead, invoices, addInvoice, onNavigateToInvoice, pendingOpenId, onPendingConsumed, materialPhotos, role="admin", currentUserName="" }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [seasonFilter, setSeasonFilter] = useState(() => {
    const saved = localStorage.getItem("taf-estimate-season-filter");
    return saved || String(new Date().getFullYear());
  });
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [commsEstimate, setCommsEstimate] = useState(null);
  const [jobBuilderEstimate, setJobBuilderEstimate] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateCatFilter, setTemplateCatFilter] = useState("all");
  const [saveSectionModal, setSaveSectionModal] = useState(null);
  const [saveSectionForm, setSaveSectionForm] = useState({ name:"", category:"Other", description:"" });
  const [templateForm, setTemplateForm] = useState(null);
  const [sectionTemplateForm, setSectionTemplateForm] = useState(null);
  const [mgmtSearch, setMgmtSearch] = useState("");
  const [mgmtCatFilter, setMgmtCatFilter] = useState("all");
  const [isDirty, setIsDirty] = useState(false);
  const [guard, setGuard] = useState(null);
  useEffect(() => { setIsDirty(false); setGuard(null); setExpandedSections({}); setEstEditTab("items"); setMatSearch(""); setMatCatFilter("all"); setEditingCaption(null); setDpEditCaption(null); setDpCaptionDraft(""); setDpConfirm(null); setVersions([]); setVersionsLoaded(false); setVersionLabelPrompt(false); setVersionLabel(""); setVersionSaving(false); setVersionSaved(false); setVersionPreview(null); setVersionRestoreConfirm(null); setVersionDeleteConfirm(null); setVersionRenaming(null); setVersionRenameDraft(""); setVersionDupWarning(null); }, [modal]);
  const tryClose = (closeFn, saveFn) => { if (!isDirty) { closeFn(); return; } setGuard({ close: closeFn, save: saveFn }); };
  const [tmplDragSec, setTmplDragSec] = useState(null);
  const [tmplOverSec, setTmplOverSec] = useState(null);
  const [tmplDragItem, setTmplDragItem] = useState(null);
  const [tmplOverItem, setTmplOverItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [autocomplete, setAutocomplete] = useState(null); // {secId, itemId, x, y, width}
  const [tmplAutocomplete, setTmplAutocomplete] = useState(null); // {si, ii, x, y, width}
  const REPORT_CONFIG_DEFAULT = {showLineItems:true,showUnitCost:true,showUnitPrice:true,showLineItemTotals:true,showSectionSubtotals:true,showTaxes:true,showReferenceMaterials:true,showDesignPlans:true};
  const [reportModalEst, setReportModalEst] = useState(null);
  const [reportConfig, setReportConfig] = useState(REPORT_CONFIG_DEFAULT);
  const [reportPreset, setReportPreset] = useState("full");
  const applyReportPreset = (preset) => {
    setReportPreset(preset);
    if (preset==="full") setReportConfig({showLineItems:true,showUnitCost:true,showUnitPrice:true,showLineItemTotals:true,showSectionSubtotals:true,showTaxes:true,showReferenceMaterials:true,showDesignPlans:true});
    else if (preset==="client") setReportConfig({showLineItems:false,showUnitCost:false,showUnitPrice:false,showLineItemTotals:false,showSectionSubtotals:true,showTaxes:true,showReferenceMaterials:true,showDesignPlans:true});
  };
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateCategory, setSaveTemplateCategory] = useState('Excavation');
  const [saveTemplateSaving, setSaveTemplateSaving] = useState(false);
  const [saveTemplateDone, setSaveTemplateDone] = useState(false);
  const handleSaveAsTemplate = async () => {
    if (!saveTemplateName.trim()) return;
    setSaveTemplateSaving(true);
    const templateDoc = {
      name: saveTemplateName.trim(),
      category: saveTemplateCategory,
      createdAt: new Date().toISOString(),
      sections: (form.sections || []).map(sec => ({
        title: sec.title || "",
        description: sec.description || "",
        items: (sec.items || []).map(it => ({
          name: it.name || "",
          category: it.category || "",
          qty: it.qty || 1,
          unit: it.unit || "",
          unitCost: it.unitCost || 0,
          unitPrice: it.unitPrice || 0,
          supplier: it.supplier || "",
        })),
      })),
    };
    await addTemplate(templateDoc);
    setSaveTemplateSaving(false);
    setSaveTemplateDone(true);
    setTimeout(() => {
      setShowSaveTemplateModal(false);
      setSaveTemplateName('');
      setSaveTemplateCategory('Excavation');
      setSaveTemplateDone(false);
    }, 1500);
  };

  const [sendingEstimateEmail, setSendingEstimateEmail] = useState(false);
  const [estimateEmailSent, setEstimateEmailSent] = useState(false);
  const handleSendEstimateEmail = async () => {
    const currentCustomer = customers.find(c => c.id === form?.customerId);
    if (!form || !currentCustomer) return;
    setSendingEstimateEmail(true);
    try {
      await sendEstimateEmail(currentCustomer, form, settings);
      setEstimateEmailSent(true);
      setTimeout(() => setEstimateEmailSent(false), 3000);
    } catch (err) {
      console.error("Failed to send estimate email:", err);
    } finally {
      setSendingEstimateEmail(false);
    }
  };

  // Row-level send email (draft status)
  const [rowEmailSending, setRowEmailSending] = useState(null);
  const [rowEmailSent, setRowEmailSent] = useState(null);
  const handleRowSendEmail = async (est) => {
    const customer = customers.find(c => c.id === est.customerId);
    if (!customer?.email || !customer?.portalToken) return;
    setRowEmailSending(est.id);
    try {
      await sendEstimateEmail(customer, est, settings);
      setRowEmailSent(est.id);
      setTimeout(() => setRowEmailSent(null), 3000);
    } catch (err) {
      console.error("Failed to send estimate email:", err);
    } finally {
      setRowEmailSending(null);
    }
  };

  // ── Autocomplete helpers ──
  const getSuggestions = (query) => {
    if (!query || query.length < 1) return [];
    return (priceList || [])
      .filter(p => p.name.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 8);
  };
  const selectSuggestion = (secId, iid, plItem) => {
    setForm(p => ({
      ...p,
      sections: p.sections.map(s => s.id !== secId ? s : {
        ...s,
        items: s.items.map(i => i.id !== iid ? i : {
          ...i,
          name: plItem.name,
          category: plItem.category || i.category,
          unitCost: plItem.cost || i.unitCost,
          unitPrice: plItem.price || i.unitPrice,
          supplier: plItem.supplier || "",
        })
      })
    }));
    setAutocomplete(null);
  };
  const openAutocomplete = (e, secId, itemId) => {
    const rect = e.target.getBoundingClientRect();
    setAutocomplete({ secId, itemId, x: rect.left, y: rect.bottom + 3, width: Math.max(rect.width, 320) });
  };

  // ── Template editor autocomplete ──
  const openTmplAutocomplete = (e, si, ii) => {
    const rect = e.target.getBoundingClientRect();
    setTmplAutocomplete({ si, ii, x: rect.left, y: rect.bottom + 3, width: Math.max(rect.width, 320) });
  };
  const selectTmplSuggestion = (si, ii, plItem) => {
    setTemplateForm(p => ({
      ...p,
      sections: p.sections.map((s, sIdx) => sIdx !== si ? s : {
        ...s,
        items: s.items.map((it, iIdx) => iIdx !== ii ? it : {
          ...it,
          name: plItem.name,
          category: plItem.category || it.category,
          unitCost: plItem.cost || it.unitCost,
          unitPrice: plItem.price || it.unitPrice,
        })
      })
    }));
    setTmplAutocomplete(null);
  };

  // showTemplates only opens when we are inside an estimate edit (form is set)
  const openTemplatesInEdit = () => setShowTemplates(true);
  const closeTemplates = () => { setShowTemplates(false); setTemplateSearch(""); setTemplateCatFilter("all"); };
  const applyTemplate = (tmpl) => {
    setForm(p => {
      if (!p) return p;
      const ts = Date.now();
      const newSections = (tmpl.sections || []).map((sec, si) => ({
        id: `s${ts}_${si}`,
        title: sec.title,
        items: (sec.items || []).map((it, ii) => ({
          id: `i${ts}_${si}_${ii}`,
          name: it.name || "",
          category: it.category || "Labor",
          qty: it.qty || 1,
          unitCost: it.unitCost || 0,
          unitPrice: it.unitPrice || 0,
        })),
      }));
      return {
        ...p,
        serviceType: tmpl.service || p.serviceType,
        // APPEND to existing sections instead of replacing
        sections: [...(p.sections || []), ...newSections],
      };
    });
    setShowTemplates(false);
  };

  const insertSectionTemplate = (tmpl) => {
    setForm(p => {
      if (!p) return p;
      const ts = Date.now();
      const newSection = {
        id: `s${ts}`,
        title: tmpl.title || tmpl.name || "Section",
        items: (tmpl.items || []).map((it, ii) => ({
          id: `i${ts}_${ii}`,
          name: it.name || "",
          category: it.category || "Labor",
          qty: it.qty || 1,
          unitCost: it.unitCost || 0,
          unitPrice: it.unitPrice || 0,
        })),
      };
      return { ...p, sections: [...(p.sections || []), newSection] };
    });
    setShowTemplates(false);
  };

  const availableSeasons = (() => {
    const years = new Set(estimates.map(e => e.createdDate ? e.createdDate.slice(0,4) : null).filter(Boolean));
    const cur = String(new Date().getFullYear());
    years.add(cur);
    return Array.from(years).sort((a,b)=>b-a);
  })();

  const seasonEstimates = seasonFilter === "all"
    ? estimates
    : estimates.filter(e => e.createdDate && e.createdDate.startsWith(seasonFilter));

  const filtered = seasonEstimates.filter(e=>{
    const c=customers.find(x=>x.id===e.customerId);
    return `${e.title} ${c?c.firstName:""} ${c?c.lastName:""}`.toLowerCase().includes(search.toLowerCase())&&(!statusFilter||e.status===statusFilter);
  });

  const today = new Date().toISOString().split("T")[0];
  const openAdd = () => { setForm({title:"",customerId:"",serviceType:"Hardscape",status:"draft",validUntil:"",createdDate:today,sections:[],number:`EST-${Math.floor(100000+Math.random()*900000)}`}); setModal("edit"); };
  useEffect(() => {
    if (preselectCustomerId) {
      setForm({title:"",customerId:preselectCustomerId,serviceType:"Hardscape",status:"draft",validUntil:"",createdDate:today,sections:[],number:`EST-${Math.floor(100000+Math.random()*900000)}`});
      setModal("edit");
      if (onPreselectConsumed) onPreselectConsumed();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectCustomerId]);
  const openEdit = (e) => { setForm({...e,sections:(e.sections||[]).map(s=>({...s,items:[...(s.items||[])]}))});setModal("edit"); };

  useEffect(() => {
    if (pendingOpenId && estimates.length > 0) {
      const e = estimates.find(x => x.id === pendingOpenId);
      if (e) { openEdit(e); onPendingConsumed(); }
    }
  // eslint-disable-next-line
  }, [estimates, pendingOpenId]);
  const saveEst = async () => {
    const existing = estimates.find(e=>e.id===form.id);
    if (!form.id) await addEstimate(form);
    else {
      await updateEstimate(form.id, form);
      if (updateLead && leads && existing && existing.status !== form.status) {
        const stageMap = { sent:"estimate-sent", approved:"won", rejected:"lost" };
        const newStage = stageMap[form.status];
        if (newStage) {
          const linked = [...(leads||[])].filter(l=>l.customerId===form.customerId).sort((a,b)=>new Date(b.date||0)-new Date(a.date||0))[0];
          if (linked) await updateLead(linked.id, {...linked, stage: newStage});
        }
      }
    }
    setModal(null);
  };
  useEffect(() => {
    if (!leads || !estimates) return;
    estimates.forEach(async (est) => {
      const stageMap = { sent:"estimate-sent", approved:"won", rejected:"lost" };
      const newStage = stageMap[est.status];
      if (!newStage) return;
      const linked = [...leads].filter(l=>l.customerId===est.customerId).sort((a,b)=>new Date(b.date||0)-new Date(a.date||0))[0];
      if (!linked) return;
      if (linked.stage===newStage) return;
      await updateLead(linked.id, {...linked, stage: newStage});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimates]);
  const handleCreateInvoiceFromEstimate = async (estimate) => {
    const sections = (estimate.sections||[]).map(sec=>({
      title: sec.title||"",
      description: sec.description||"",
      price: (sec.items||[]).reduce((sum,it)=>sum+(it.qty||0)*(it.unitPrice||0),0),
    }));
    const subtotal = sections.reduce((s,sec)=>s+sec.price,0);
    const tps = subtotal*0.05;
    const tvq = subtotal*0.09975;
    const total = subtotal+tps+tvq;
    const linkedJob = [...(jobs||[])].filter(j=>j.customerId===estimate.customerId&&j.startDate).sort((a,b)=>new Date(b.startDate)-new Date(a.startDate))[0];
    const jobStartDate = linkedJob?.startDate||null;
    const depositDate = jobStartDate ? new Date(new Date(jobStartDate).getTime()-21*24*60*60*1000).toISOString().split("T")[0] : "";
    const paymentSchedule = [
      { id:crypto.randomUUID(), label:"Dépôt", percentage:50, amount:Math.round(total*0.50*100)/100, dueDate:depositDate, dueTrigger:"date", status:"pending" },
      { id:crypto.randomUUID(), label:"En cours de travaux", percentage:25, amount:Math.round(total*0.25*100)/100, dueDate:"", dueTrigger:"date", status:"pending" },
      { id:crypto.randomUUID(), label:"Solde final", percentage:25, amount:Math.round(total*0.25*100)/100, dueDate:"", dueTrigger:"date", status:"pending" },
    ];
    const ref = await addInvoice({
      customerId: estimate.customerId,
      estimateId: estimate.id,
      number: `F-${Date.now()}`,
      status: "draft",
      isPublished: false,
      issuedAt: new Date().toISOString().split("T")[0],
      sections,
      paymentSchedule,
      changeOrders: [],
    });
    if (onNavigateToInvoice && ref?.id) onNavigateToInvoice(ref.id);
  };
  const saveSectionTemplateForm = async () => {
    const {id, _type, _title, _cat, ...rest} = sectionTemplateForm||{};
    const payload = {...rest, title:(rest.title||"").trim(), items:(rest.items||[]).map(({id:_id,...it})=>it), createdAt:(sectionTemplateForm?.createdAt)||new Date().toISOString()};
    if(id) await updateSectionTemplate(id, payload); else await addSectionTemplate(payload);
    setModal("templateManager");
  };
  const saveTemplateFormFn = async () => {
    if(templateForm?.id) await updateTemplate(templateForm.id, templateForm); else await addTemplate(templateForm);
    setModal("templateManager");
  };

  const printEstimate = (est) => {
    setModal(null);
    setReportModalEst(est);
    setReportConfig({showLineItems:true,showUnitCost:true,showUnitPrice:true,showLineItemTotals:true,showSectionSubtotals:true,showTaxes:true});
    setReportPreset("full");
  };
  const doPrintEstimate = () => {
    const customer = customers.find(c => c.id === reportModalEst.customerId);
    const html = generateReportHTML("Soumission", reportModalEst, customer, settings, reportConfig, priceList);
    const win = window.open("", "_blank", "width=960,height=800,scrollbars=yes");
    if (win) { win.document.write(html); win.document.close(); }
    setReportModalEst(null);
  };

  const addSection = () => setForm(p=>({...p,sections:[...p.sections,{id:`s${Date.now()}`,title:"New Section",items:[]}]}));
  const updateSection = (sid,field,value) => setForm(p=>({...p,sections:p.sections.map(s=>s.id===sid?{...s,[field]:value}:s)}));
  const removeSection = (sid) => setForm(p=>({...p,sections:p.sections.filter(s=>s.id!==sid)}));
  const addItem = (sid) => setForm(p=>({...p,sections:p.sections.map(s=>s.id===sid?{...s,items:[...s.items,{id:`i${Date.now()}`,name:"",category:"Labor",qty:1,unitCost:0,unitPrice:0}]}:s)}));
  const addSubheading = (sid) => setForm(p=>({...p,sections:p.sections.map(s=>s.id===sid?{...s,items:[...s.items,{id:`h${Date.now()}`,type:"subheading",label:""}]}:s)}));
  const insertItemAfter = (sid, afterIid) => setForm(p=>({...p,sections:p.sections.map(s=>{if(s.id!==sid)return s;const idx=s.items.findIndex(i=>i.id===afterIid);const newItem={id:`i${Date.now()}`,name:"",category:"Labor",qty:1,unitCost:0,unitPrice:0};const items=[...s.items];items.splice(idx+1,0,newItem);return {...s,items};})}));
  const [addDropdown, setAddDropdown] = useState(null); // secId of open dropdown
  // ── Materials tab state ──
  const [estEditTab, setEstEditTab] = useState("items"); // "items"|"materials"
  const [matSearch, setMatSearch] = useState("");
  const [matCatFilter, setMatCatFilter] = useState("all");
  const [editingCaption, setEditingCaption] = useState(null); // materialPhotoId being edited
  const [captionDraft, setCaptionDraft] = useState("");
  const [dpUploading, setDpUploading] = useState(false);
  const [dpEditCaption, setDpEditCaption] = useState(null); // plan.id being caption-edited
  const [dpCaptionDraft, setDpCaptionDraft] = useState("");
  const [dpConfirm, setDpConfirm] = useState(null); // plan object to confirm-delete
  const [plModal, setPlModal] = useState(null); // {form, triggerKey, dupWarning, matchId}
  const [plFlash, setPlFlash] = useState(null); // itemId with green flash
  const [plSupplierAC, setPlSupplierAC] = useState(false);
  const plSuppliers = [...new Set((priceList||[]).map(i=>i.supplier).filter(Boolean))].sort();
  const plSupplierSuggestions = plSupplierAC && plModal ? plSuppliers.filter(s=>!plModal.form.supplier||s.toLowerCase().includes(plModal.form.supplier.toLowerCase())) : [];
  // ── Version snapshot state ──
  const [versions, setVersions] = useState([]);
  const [versionsLoaded, setVersionsLoaded] = useState(false);
  const [versionLabelPrompt, setVersionLabelPrompt] = useState(false);
  const [versionLabel, setVersionLabel] = useState("");
  const [versionSaving, setVersionSaving] = useState(false);
  const [versionSaved, setVersionSaved] = useState(false);
  const [versionPreview, setVersionPreview] = useState(null);
  const [versionRestoreConfirm, setVersionRestoreConfirm] = useState(null);
  const [versionDeleteConfirm, setVersionDeleteConfirm] = useState(null); // { ver }
  const [versionRenaming, setVersionRenaming] = useState(null); // { verId }
  const [versionRenameDraft, setVersionRenameDraft] = useState("");
  const [versionDupWarning, setVersionDupWarning] = useState(null); // { matchVer }

  const openPlModal = (it) => {
    setPlModal({
      form:{name:it.name||"",category:it.category||"Material",unit:it.unit||"",cost:it.unitCost||0,price:it.unitPrice||0,supplier:it.supplier||""},
      triggerKey:it.id, dupWarning:false, matchId:null,
    });
  };
  const savePlItem = async () => {
    if(!plModal||!addPriceItem||!updatePriceItem) return;
    const item = {...plModal.form, cost:+plModal.form.cost||0, price:+plModal.form.price||0};
    const match = (priceList||[]).find(p=>p.name.trim().toLowerCase()===item.name.trim().toLowerCase());
    if(match && !plModal.dupWarning) {
      setPlModal(p=>({...p, dupWarning:true, matchId:match.id}));
      return;
    }
    if(match && plModal.matchId) await updatePriceItem(plModal.matchId, item);
    else await addPriceItem(item);
    const key = plModal.triggerKey;
    setPlModal(null);
    setPlFlash(key);
    setTimeout(()=>setPlFlash(null), 1500);
  };
  const updateItem = (sid,iid,k,v) => setForm(p=>({...p,sections:p.sections.map(s=>s.id===sid?{...s,items:s.items.map(i=>i.id===iid?{...i,[k]:k==="category"||k==="name"?v:+v||0}:i)}:s)}));
  const removeItem = (sid,iid) => setForm(p=>({...p,sections:p.sections.map(s=>s.id===sid?{...s,items:s.items.filter(i=>i.id!==iid)}:s)}));
  const [innerConfirm, setInnerConfirm] = useState(null); // {type:"section"|"item", sid, iid, label}
  const [dragSection, setDragSection] = useState(null);
  const [dragItem, setDragItem] = useState(null); // {secId, itemId}
  const [overSection, setOverSection] = useState(null);
  const [overItem, setOverItem] = useState(null); // {secId, itemId}

  // ── Section drag handlers ──
  const onSectionDragStart = (e, sid) => { setDragSection(sid); e.stopPropagation(); };
  const onSectionDragOver = (e, sid) => { e.preventDefault(); e.stopPropagation(); setOverSection(sid); };
  const onSectionDrop = (e, targetSid) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragSection || dragSection === targetSid) { setDragSection(null); setOverSection(null); return; }
    setForm(p => {
      const secs = [...p.sections];
      const fromIdx = secs.findIndex(s => s.id === dragSection);
      const toIdx = secs.findIndex(s => s.id === targetSid);
      const [moved] = secs.splice(fromIdx, 1);
      secs.splice(toIdx, 0, moved);
      return { ...p, sections: secs };
    });
    setDragSection(null); setOverSection(null);
  };

  // ── Item drag handlers ──
  const onItemDragStart = (e, secId, itemId) => { e.stopPropagation(); setDragItem({ secId, itemId }); };
  const onItemDragOver = (e, secId, itemId) => { e.preventDefault(); e.stopPropagation(); setOverItem({ secId, itemId }); };
  const onItemDrop = (e, targetSecId, targetItemId) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragItem) { setDragItem(null); setOverItem(null); return; }
    const { secId: fromSecId, itemId: fromItemId } = dragItem;
    setForm(p => {
      const secs = p.sections.map(s => ({ ...s, items: [...s.items] }));
      const fromSec = secs.find(s => s.id === fromSecId);
      const toSec = secs.find(s => s.id === targetSecId);
      if (!fromSec || !toSec) return p;
      const fromIdx = fromSec.items.findIndex(i => i.id === fromItemId);
      const [movedItem] = fromSec.items.splice(fromIdx, 1);
      const toIdx = toSec.items.findIndex(i => i.id === targetItemId);
      toSec.items.splice(toIdx >= 0 ? toIdx : toSec.items.length, 0, movedItem);
      return { ...p, sections: secs };
    });
    setDragItem(null); setOverItem(null);
  };

  // ── Reorder by number input ──
  const reorderSectionByNum = (sid, newNum) => {
    if (!form) return;
    const n = Math.max(1, Math.min(form.sections.length, +newNum || 1)) - 1;
    setForm(p => {
      if (!p) return p;
      const secs = [...p.sections];
      const fromIdx = secs.findIndex(s => s.id === sid);
      const [moved] = secs.splice(fromIdx, 1);
      secs.splice(n, 0, moved);
      return { ...p, sections: secs };
    });
  };
  const reorderItemByNum = (secId, iid, newNum) => {
    setForm(p => {
      if (!p) return p;
      const secs = p.sections.map(s => {
        if (s.id !== secId) return s;
        const items = [...s.items];
        const fromIdx = items.findIndex(i => i.id === iid);
        const n = Math.max(1, Math.min(items.length, +newNum || 1)) - 1;
        const [moved] = items.splice(fromIdx, 1);
        items.splice(n, 0, moved);
        return { ...s, items };
      });
      return { ...p, sections: secs };
    });
  };

  const toggleSection = (sid) => setExpandedSections(p=>({...p,[sid]:!p[sid]}));
  const sectionTotal = (sec) => (sec.items||[]).reduce((a,i)=>a+(i.unitPrice||0)*(i.qty||1),0);
  const estSubtotal = (f) => (f.sections||[]).reduce((a,s)=>a+sectionTotal(s),0);

  // ── Version helpers ──
  const loadVersions = async (estimateId) => {
    const snap = await getDocs(query(collection(db, "estimates", estimateId, "versions"), orderBy("versionNumber", "desc")));
    setVersions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setVersionsLoaded(true);
  };

  const buildVersionDoc = (sectionsSnap, label, nextNum) => {
    const sub = calcEstimateTotal({ sections: sectionsSnap });
    const tps = sub * 0.05;
    const tvq = sub * 0.09975;
    return {
      versionNumber: nextNum,
      label: label || "",
      savedAt: serverTimestamp(),
      savedBy: currentUserName,
      sections: JSON.parse(JSON.stringify(sectionsSnap)),
      totalBeforeTax: Math.round(sub * 100) / 100,
      totalWithTax: Math.round((sub + tps + tvq) * 100) / 100,
      sectionCount: (sectionsSnap || []).filter(s => s.type !== "group").length,
    };
  };

  const doSaveVersion = async (force = false) => {
    if (!form?.id) return;
    if (!force) {
      // Redundancy check: same sectionCount + totalBeforeTax + totalWithTax = likely duplicate
      const sub = calcEstimateTotal({ sections: form.sections });
      const chkBefore = Math.round(sub * 100) / 100;
      const chkWith = Math.round((sub + sub * 0.05 + sub * 0.09975) * 100) / 100;
      const chkCount = (form.sections || []).filter(s => s.type !== "group").length;
      const dupMatch = versions.find(v => v.sectionCount === chkCount && v.totalBeforeTax === chkBefore && v.totalWithTax === chkWith);
      if (dupMatch) { setVersionDupWarning({ matchVer: dupMatch }); return; }
    }
    setVersionDupWarning(null);
    setVersionSaving(true);
    const nextNum = versions.length > 0 ? Math.max(...versions.map(v => v.versionNumber || 0)) + 1 : 1;
    // Firestore security: versions subcollection requires auth — never exposed to unauthenticated portal reads
    await addDoc(collection(db, "estimates", form.id, "versions"), buildVersionDoc(form.sections, versionLabel.trim(), nextNum));
    await loadVersions(form.id);
    setVersionSaving(false);
    setVersionLabelPrompt(false);
    setVersionLabel("");
    setVersionSaved(true);
    setTimeout(() => setVersionSaved(false), 1500);
  };

  const doDeleteVersion = async (ver) => {
    if (!form?.id) return;
    await deleteDoc(doc(db, "estimates", form.id, "versions", ver.id));
    await loadVersions(form.id);
    setVersionDeleteConfirm(null);
  };

  const doRenameVersion = async (verId, newLabel) => {
    if (!newLabel.trim() || !form?.id) return;
    await updateDoc(doc(db, "estimates", form.id, "versions", verId), { label: newLabel.trim() });
    await loadVersions(form.id);
    setVersionRenaming(null);
    setVersionRenameDraft("");
  };

  const doRestoreVersion = async (ver) => {
    const estimateId = form.id;
    if (!estimateId) return;
    const nextNum = versions.length > 0 ? Math.max(...versions.map(v => v.versionNumber || 0)) + 1 : 1;
    await addDoc(collection(db, "estimates", estimateId, "versions"), buildVersionDoc(form.sections, t.estimates.versionBeforeRestore, nextNum));
    setForm(p => ({ ...p, sections: JSON.parse(JSON.stringify(ver.sections || [])) }));
    setIsDirty(true);
    setVersionRestoreConfirm(null);
    await loadVersions(estimateId);
  };

  const fmtVerDate = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) + " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div><h1>{t.estimates.title}</h1><p>{t.estimates.subtitle}</p></div>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-secondary" onClick={()=>setModal("templateManager")}><Icon name="template" size={14}/>{t.estimates.templatesBtn}</button>
            <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={14}/>{t.estimates.newBtn}</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        {(()=>{
          const te = t.estimates;
          const STAGE_DEFS = [
            { key:"all",    label:te.trackerAll,      color:"#6b7280", bg:"#f3f4f6" },
            { key:"draft",  label:te.trackerDraft,    color:"#6b7280", bg:"#f3f4f6" },
            { key:"sent",   label:te.trackerSent,     color:"#1d4ed8", bg:"#dbeafe" },
            { key:"approved",label:te.trackerApproved,color:"#166534", bg:"#dcfce7" },
            { key:"revised",label:te.trackerRevised,  color:"#854d0e", bg:"#fef9c3" },
            { key:"rejected",label:te.trackerRejected,color:"#dc2626", bg:"#fee2e2" },
          ];
          const sentTotal   = seasonEstimates.filter(e=>["sent","approved","rejected","revised"].includes(e.status)).reduce((a,e)=>a+calcEstimateTotal(e),0);
          const wonTotal    = seasonEstimates.filter(e=>e.status==="approved").reduce((a,e)=>a+calcEstimateTotal(e),0);
          const invoicedTotal = (invoices||[]).filter(inv=>{
            const est = seasonEstimates.find(e=>e.id===inv.estimateId);
            return !!est;
          }).reduce((a,inv)=>{
            const sub = (inv.sections||[]).reduce((b,sec)=>b+(sec.price!=null?sec.price:(sec.items||[]).reduce((c,i)=>c+(i.unitPrice||0)*(i.qty||1),0)),0);
            return a+sub;
          },0);
          const wonCount      = seasonEstimates.filter(e=>e.status==="approved").length;
          const rejectedCount = seasonEstimates.filter(e=>e.status==="rejected").length;
          const resolvedCount = wonCount + rejectedCount;
          const winRate       = resolvedCount>0 ? Math.round((wonCount/resolvedCount)*100) : null;
          return (
            <div className="est-tracker">
              <div className="est-tracker-head">
                <span className="est-tracker-title">{te.trackerSeason}</span>
                <select
                  className="est-tracker-season"
                  value={seasonFilter}
                  onChange={e=>{ const v=e.target.value; setSeasonFilter(v); localStorage.setItem("taf-estimate-season-filter",v); }}
                >
                  <option value="all">{te.trackerSeasonAll}</option>
                  {availableSeasons.map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="est-tracker-cards">
                {STAGE_DEFS.map(({key,label,color})=>{
                  const count = key==="all" ? seasonEstimates.length : seasonEstimates.filter(e=>e.status===key).length;
                  const value = key==="all"
                    ? seasonEstimates.reduce((a,e)=>a+calcEstimateTotal(e),0)
                    : seasonEstimates.filter(e=>e.status===key).reduce((a,e)=>a+calcEstimateTotal(e),0);
                  const isActive = key==="all" ? statusFilter==="" : statusFilter===key;
                  return (
                    <div
                      key={key}
                      className={`est-tracker-card${isActive?" tc-active":""}`}
                      style={{borderTopColor:color}}
                      onClick={()=>{ setStatusFilter(isActive?"":key==="all"?"":key); }}
                    >
                      <div className="est-tracker-card-label" style={{color}}>{label}</div>
                      <div className="est-tracker-card-count">{count}</div>
                      <div className="est-tracker-card-value">{fmt(value)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="est-tracker-summary">
                <div className="est-tracker-metric">
                  <div className="est-tracker-metric-label">{te.trackerSentTotal}</div>
                  <div className="est-tracker-metric-value">{fmt(sentTotal)}</div>
                </div>
                <div className="est-tracker-metric">
                  <div className="est-tracker-metric-label">{te.trackerWon}</div>
                  <div className="est-tracker-metric-value">{fmt(wonTotal)}</div>
                </div>
                <div className="est-tracker-metric">
                  <div className="est-tracker-metric-label">{te.trackerInvoiced}</div>
                  <div className="est-tracker-metric-value">{fmt(invoicedTotal)}</div>
                </div>
                {winRate!==null&&<div className="est-tracker-metric">
                  <div className="est-tracker-metric-label">{te.trackerWinRate}</div>
                  <div className="est-tracker-metric-value">{winRate}%</div>
                </div>}
              </div>
            </div>
          );
        })()}
        <div className="toolbar">
          <div className="search-wrap"><Icon name="search"/><input className="search-input" placeholder={t.estimates.search} value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <select className="filter-select" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="">{t.estimates.allStatuses}</option>{[["draft","Draft"],["sent","Sent"],["revised","Revised"],["approved","Approved"],["rejected","Rejected"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>
        </div>
        <div className="card">
          {filtered.map(e=>{
            const c=customers.find(x=>x.id===e.customerId);
            return (
              <div key={e.id} className="list-row">
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontWeight:600,fontSize:14}}>{e.title}</span>
                    {/* Quick status change — click badge to cycle */}
                    <select
                      className="form-select"
                      value={e.status||"draft"}
                      onClick={ev=>ev.stopPropagation()}
                      onChange={async ev=>{
                        ev.stopPropagation();
                        await updateEstimate(e.id,{...e,status:ev.target.value});
                      }}
                      style={{
                        padding:"2px 20px 2px 8px",fontSize:11,fontWeight:600,height:"auto",
                        background:({draft:"#f3f4f6",sent:"#dbeafe",revised:"#fef9c3",approved:"#dcfce7",rejected:"#fee2e2"})[e.status]||"#f3f4f6",
                        color:({draft:"#6b7280",sent:"#1d4ed8",revised:"#854d0e",approved:"#166534",rejected:"#dc2626"})[e.status]||"#6b7280",
                        border:"none",borderRadius:20,cursor:"pointer",
                      }}
                    >
                      {[["draft","Draft"],["sent","Sent"],["revised","Revised"],["approved","Approved"],["rejected","Rejected"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                    </select>
                    <span style={{fontSize:12,color:"var(--text-3)"}}>{e.number}</span>
                    {(()=>{const inv=(invoices||[]).find(inv=>inv.estimateId===e.id);if(!inv)return null;return inv.isPublished?(<span style={{fontSize:10,fontWeight:600,background:"#dcfce7",color:"#16a34a",border:"1px solid #bbf7d0",borderRadius:4,padding:"1px 6px",marginLeft:4,whiteSpace:"nowrap"}}>{t.estimates.invoicePublished}</span>):(<span style={{fontSize:10,fontWeight:600,background:"#fef3c7",color:"#d97706",border:"1px solid #fde68a",borderRadius:4,padding:"1px 6px",marginLeft:4,whiteSpace:"nowrap"}}>{t.estimates.invoiceUnpublished}</span>);})()}
                  </div>
                  <div style={{display:"flex",gap:12,marginTop:3,fontSize:12,color:"var(--text-3)"}}>
                    {c&&<span>{c.firstName} {c.lastName}</span>}<span>{e.serviceType}</span>
                    <span className="green-text">{fmt(calcEstimateTotal(e))}</span>
                    <span>{countItems(e)} {t.estimates.items}</span>
                    {e.validUntil&&<span>{t.estimates.validUntil} {e.validUntil}</span>}
                  </div>
                </div>
                <div className="list-row-actions">
                  {/* Contextual next-step button */}
                  {e.status==="draft"&&(()=>{const cust=customers.find(x=>x.id===e.customerId);if(!cust?.email||!cust?.portalToken)return null;const sending=rowEmailSending===e.id,sent=rowEmailSent===e.id;return <button key="send" className="btn btn-secondary btn-sm" onClick={()=>handleRowSendEmail(e)} disabled={sending} style={{fontSize:11,padding:"3px 10px",whiteSpace:"nowrap"}}>{sent?"✓ Envoyé":sending?"...":"Envoyer"}</button>;})()}
                  {(e.status==="sent"||e.status==="revised")&&<button className="btn btn-secondary btn-sm" onClick={()=>setCommsEstimate(e)} style={{fontSize:11,padding:"3px 10px",whiteSpace:"nowrap"}}>Relancer</button>}
                  {e.status==="rejected"&&<button className="btn btn-secondary btn-sm" onClick={()=>openEdit(e)} style={{fontSize:11,padding:"3px 10px",whiteSpace:"nowrap"}}>Réviser</button>}
                  <button className="btn-icon" title="Communications" onClick={()=>setCommsEstimate(e)}><Icon name="phone" size={14}/></button>
                  {(e.status==="approved")&&(
                    <button className="btn-icon" title="Créer un travail" onClick={()=>setJobBuilderEstimate(e)} style={{color:"var(--green)"}}><Icon name="jobs" size={14}/></button>
                  )}
                  {(e.status==="approved")&&!(invoices||[]).some(inv=>inv.estimateId===e.id)&&(
                    <button className="btn btn-primary btn-sm" onClick={()=>handleCreateInvoiceFromEstimate(e)} style={{fontSize:11,padding:"3px 10px"}}>
                      <Icon name="invoice" size={12}/>Créer une facture
                    </button>
                  )}
                  <EstimateBadge estimateId={e.id}/>
                  <button className="btn-icon" title="Imprimer / Print" onClick={()=>printEstimate(e)}><Icon name="printer" size={14}/></button>
                  <button className="btn-icon" title={t.common.edit} onClick={()=>openEdit(e)}><Icon name="edit" size={14}/></button>
                  <button className="btn-icon danger" title={t.common.delete} onClick={()=>setConfirmDelete(e)}><Icon name="trash" size={14}/></button>
                </div>
              </div>
            );
          })}
          {filtered.length===0&&<div className="empty-state"><div className="empty-icon"><Icon name="estimates" size={36}/></div><div className="empty-title">{t.estimates.title}</div></div>}
        </div>
      </div>

      {showTemplates&&(
        <div className="modal-backdrop" style={{zIndex:200}} onClick={e=>e.target===e.currentTarget&&closeTemplates()}>
          <div className="modal">
            <div className="modal-header">
              <div><div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}><Icon name="template" size={16}/>{t.estimates.templateTitle}</div></div>
              <button className="btn-icon" title={t.common.close} onClick={closeTemplates}><Icon name="x"/></button>
            </div>
            {/* Unified search + filter */}
            {(()=>{
              const sectionRows = (sectionTemplates||[]).map(tmpl=>({...tmpl, _type:"section", _title:(tmpl.title||tmpl.name||""), _cat:(tmpl.category||"")}));
              const activitySource = (templates||[]).length===0 ? ESTIMATE_TEMPLATES : templates;
              const activityRows = activitySource.map(tmpl=>({...tmpl, _type:"activity", _title:(tmpl.name||""), _cat:(tmpl.service||"")}));
              const allRows = [...sectionRows, ...activityRows].sort((a,b)=>a._title.localeCompare(b._title));
              const cats = ["all", ...Array.from(new Set(allRows.map(r=>r._cat).filter(Boolean))).sort()];
              const q = templateSearch.toLowerCase();
              const visible = allRows.filter(r=>
                (!q || r._title.toLowerCase().includes(q)) &&
                (templateCatFilter==="all" || r._cat===templateCatFilter)
              );
              return (
                <>
                  <div style={{padding:"12px 16px 0",display:"flex",flexDirection:"column",gap:10}}>
                    <input
                      className="form-input"
                      placeholder={t.estimates.templateSearch}
                      value={templateSearch}
                      onChange={e=>setTemplateSearch(e.target.value)}
                      autoFocus
                    />
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {cats.map(c=>(
                        <button key={c} onClick={()=>setTemplateCatFilter(c)} className={`tmpl-pill${templateCatFilter===c?" tmpl-pill-active":""}`}>
                          {c==="all" ? t.estimates.templateAll : c}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="modal-body" style={{padding:"8px 0"}}>
                    {visible.length===0
                      ? <div className="empty-state" style={{padding:"32px 20px"}}><div className="empty-icon"><Icon name="template" size={32}/></div><div className="empty-title">{t.estimates.noSectionTemplates}</div></div>
                      : visible.map((tmpl,ti)=>(
                          <div key={tmpl.id||ti} className="list-row" style={{cursor:"pointer"}} onClick={()=>tmpl._type==="section"?insertSectionTemplate(tmpl):applyTemplate(tmpl)}>
                            <div style={{flex:1}}>
                              <div style={{display:"flex",alignItems:"center",gap:7}}>
                                <span style={{fontWeight:600,fontSize:14}}>{tmpl._title}</span>
                                <span className="tmpl-type-badge">{tmpl._type==="section"?t.estimates.templateSectionBadge:t.estimates.templateActivityBadge}</span>
                              </div>
                              {tmpl._cat&&<div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{tmpl._cat}</div>}
                            </div>
                            <Icon name="chevronRight" size={16}/>
                          </div>
                        ))
                    }
                  </div>
                </>
              );
            })()}
            <div className="modal-footer"><button className="btn btn-secondary" onClick={closeTemplates}>{t.estimates.cancel}</button></div>
          </div>
        </div>
      )}

      {/* ── SAVE SECTION AS TEMPLATE ── */}
      {saveSectionModal&&(
        <div className="modal-backdrop" style={{zIndex:250}} onClick={e=>e.target===e.currentTarget&&setSaveSectionModal(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <div><div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}><Icon name="bookmark" size={16}/>{t.estimates.saveSectionBtn}</div></div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setSaveSectionModal(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t.estimates.sectionTemplateName}</label>
                <input className="form-input" value={saveSectionForm.name} onChange={e=>setSaveSectionForm(p=>({...p,name:e.target.value}))} autoFocus/>
              </div>
              <div className="form-group">
                <label className="form-label">{t.estimates.category}</label>
                <select className="form-select" value={saveSectionForm.category} onChange={e=>setSaveSectionForm(p=>({...p,category:e.target.value}))}>
                  {["Material","Labor","Equipment","Subcontractor","Other"].map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t.estimates.sectionTemplateDesc}</label>
                <input className="form-input" value={saveSectionForm.description} onChange={e=>setSaveSectionForm(p=>({...p,description:e.target.value}))}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setSaveSectionModal(null)}>{t.estimates.cancel}</button>
              <button className="btn btn-primary" disabled={!saveSectionForm.name.trim()} onClick={async()=>{
                await addSectionTemplate({ title:saveSectionForm.name.trim(), category:saveSectionForm.category, description:saveSectionForm.description.trim(), items:(saveSectionModal.sec.items||[]).map(({id:_,...it})=>it), createdAt:new Date().toISOString() });
                setSaveSectionModal(null);
              }}><Icon name="bookmark" size={14}/>{t.estimates.saveSectionBtn}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TEMPLATE MANAGER ── */}
      {modal==="templateManager"&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <div><div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}><Icon name="template" size={16}/>{t.estimates.templatesBtn}</div></div>
              <div style={{display:"flex",gap:6}}>
                <button className="btn btn-primary btn-sm" onClick={()=>setModal("templateTypeNew")}><Icon name="plus" size={12}/>New</button>
                <button className="btn-icon" title={t.common.close} onClick={()=>setModal(null)}><Icon name="x"/></button>
              </div>
            </div>
            {(()=>{
              const sRows = (sectionTemplates||[]).map(tmpl=>({...tmpl,_type:"section",_title:(tmpl.title||tmpl.name||""),_cat:(tmpl.category||"")}));
              const aRows = (templates||[]).map(tmpl=>({...tmpl,_type:"activity",_title:(tmpl.name||""),_cat:(tmpl.service||"")}));
              const allRows = [...sRows,...aRows].sort((a,b)=>a._title.localeCompare(b._title));
              const cats = ["all",...Array.from(new Set(allRows.map(r=>r._cat).filter(Boolean))).sort()];
              const q = mgmtSearch.toLowerCase();
              const visible = allRows.filter(r=>(!q||r._title.toLowerCase().includes(q))&&(mgmtCatFilter==="all"||r._cat===mgmtCatFilter));
              return (
                <>
                  <div style={{padding:"12px 16px 0",display:"flex",flexDirection:"column",gap:10}}>
                    <input className="form-input" placeholder={t.estimates.templateSearch} value={mgmtSearch} onChange={e=>setMgmtSearch(e.target.value)} autoFocus/>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {cats.map(c=>(
                        <button key={c} onClick={()=>setMgmtCatFilter(c)} className={`tmpl-pill${mgmtCatFilter===c?" tmpl-pill-active":""}`}>
                          {c==="all"?t.estimates.templateAll:c}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="modal-body" style={{padding:"8px 0"}}>
                    {visible.length===0
                      ? <div className="empty-state" style={{padding:"32px 20px"}}><div className="empty-icon"><Icon name="template" size={32}/></div><div className="empty-title">No templates yet</div><div className="empty-sub">Click "+ New" to create your first template</div></div>
                      : visible.map((tmpl,ti)=>(
                          <div key={tmpl.id||ti} className="list-row">
                            <div style={{flex:1}}>
                              <div style={{display:"flex",alignItems:"center",gap:7}}>
                                <span style={{fontWeight:600,fontSize:14}}>{tmpl._title}</span>
                                <span className="tmpl-type-badge">{tmpl._type==="section"?t.estimates.templateSectionBadge:t.estimates.templateActivityBadge}</span>
                              </div>
                              {tmpl._cat&&<div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{tmpl._cat}</div>}
                            </div>
                            <div style={{display:"flex",gap:4}}>
                              <button className="btn-icon" title={t.tooltips.editTemplate} onClick={()=>{
                                if(tmpl._type==="section"){setSectionTemplateForm({...tmpl});setModal("sectionTemplateEdit");}
                                else{setTemplateForm({...tmpl});setModal("templateEdit");}
                              }}><Icon name="edit" size={13}/></button>
                              <button className="btn-icon danger" title={t.tooltips.deleteTemplate} onClick={()=>setConfirmDelete({id:tmpl.id,_type:tmpl._type==="section"?"sectionTemplate":"template",name:tmpl._title})}><Icon name="trash" size={13}/></button>
                            </div>
                          </div>
                        ))
                    }
                  </div>
                </>
              );
            })()}
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(null)}>{t.estimates.cancel}</button></div>
          </div>
        </div>
      )}

      {/* ── TEMPLATE TYPE PICKER ── */}
      {modal==="templateTypeNew"&&(
        <div className="modal-backdrop" style={{zIndex:210}} onClick={e=>e.target===e.currentTarget&&setModal("templateManager")}>
          <div className="modal" style={{maxWidth:360}}>
            <div className="modal-header">
              <div className="modal-title">{t.estimates.templateChooseType}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setModal("templateManager")}><Icon name="x"/></button>
            </div>
            <div className="modal-body" style={{display:"flex",gap:16,justifyContent:"center",padding:"28px 24px"}}>
              <button className="tmpl-type-pick-btn" onClick={()=>{setSectionTemplateForm({title:"",category:"",description:"",items:[]});setModal("sectionTemplateEdit");}}>
                <Icon name="bookmark" size={24}/>
                <span>{t.estimates.templateNewSection}</span>
              </button>
              <button className="tmpl-type-pick-btn" onClick={()=>{setTemplateForm({name:"",service:"Hardscape",desc:""});setModal("templateEdit");}}>
                <Icon name="template" size={24}/>
                <span>{t.estimates.templateNewActivity}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION TEMPLATE EDITOR ── */}
      {modal==="sectionTemplateEdit"&&sectionTemplateForm&&(
        <div className="modal-backdrop" style={{zIndex:210}} onClick={e=>e.target===e.currentTarget&&tryClose(()=>setModal("templateManager"),saveSectionTemplateForm)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div><div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}><Icon name="bookmark" size={16}/>{sectionTemplateForm.id?t.estimates.templateSectionBadge+" — "+sectionTemplateForm.title:t.estimates.templateNewSection}</div></div>
              <button className="btn-icon" title={t.common.close} onClick={()=>tryClose(()=>setModal("templateManager"),saveSectionTemplateForm)}><Icon name="x"/></button>
            </div>
            <div className="modal-body" onChange={()=>setIsDirty(true)}>
              <div className="form-grid form-grid-2" style={{marginBottom:16}}>
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label className="form-label">Title *</label>
                  <input className="form-input" value={sectionTemplateForm.title||""} onChange={e=>setSectionTemplateForm(p=>({...p,title:e.target.value}))} autoFocus/>
                </div>
                <div className="form-group">
                  <label className="form-label">{t.estimates.category}</label>
                  <select className="form-select" value={sectionTemplateForm.category||""} onChange={e=>setSectionTemplateForm(p=>({...p,category:e.target.value}))}>
                    <option value="">{t.estimates.templateCategoryPlaceholder}</option>
                    {["Excavation","Hardscape","Landscape Design","Pool","Other"].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t.estimates.sectionTemplateDesc}</label>
                  <input className="form-input" value={sectionTemplateForm.description||""} onChange={e=>setSectionTemplateForm(p=>({...p,description:e.target.value}))}/>
                </div>
              </div>
              <div className="divider"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:14}}>{t.estimates.lineItems}</div>
                <button className="btn btn-secondary btn-sm" onClick={()=>setSectionTemplateForm(p=>({...p,items:[...(p.items||[]),{id:`i${Date.now()}`,name:"",category:"Labor",qty:1,unit:"",unitCost:0,unitPrice:0}]}))}>
                  <Icon name="plus" size={12}/>{t.estimates.addItem}
                </button>
              </div>
              {(sectionTemplateForm.items||[]).length>0&&(
                <div style={{display:"grid",gridTemplateColumns:"1fr 110px 70px 80px 90px 90px 28px",gap:6,padding:"5px 8px",background:"var(--bg)",borderBottom:"1px solid var(--border)",borderRadius:"6px 6px 0 0"}}>
                  {["Item","Category","Qty","Unit","Unit Cost","Unit Price",""].map((h,i)=>(
                    <span key={i} style={{fontSize:10,fontWeight:600,letterSpacing:".06em",textTransform:"uppercase",color:"var(--text-3)"}}>{h}</span>
                  ))}
                </div>
              )}
              {(sectionTemplateForm.items||[]).map((it,ii)=>(
                <div key={it.id||ii} style={{display:"grid",gridTemplateColumns:"1fr 110px 70px 80px 90px 90px 28px",gap:6,padding:"7px 8px",borderBottom:"1px solid var(--border)",alignItems:"center"}}>
                  <input className="form-input" style={{fontSize:12}} value={it.name||""} placeholder="Item name" onChange={e=>setSectionTemplateForm(p=>({...p,items:p.items.map((x,j)=>j===ii?{...x,name:e.target.value}:x)}))}/>
                  <select className="form-select" style={{fontSize:11,padding:"4px 20px 4px 6px"}} value={it.category||"Labor"} onChange={e=>setSectionTemplateForm(p=>({...p,items:p.items.map((x,j)=>j===ii?{...x,category:e.target.value}:x)}))}>
                    {["Labor","Equipment","Material","Subcontractor"].map(c=><option key={c}>{c}</option>)}
                  </select>
                  <input className="form-input" style={{fontSize:12}} type="number" value={it.qty||1} onChange={e=>setSectionTemplateForm(p=>({...p,items:p.items.map((x,j)=>j===ii?{...x,qty:+e.target.value||1}:x)}))} onFocus={e=>e.target.select()}/>
                  <input className="form-input" style={{fontSize:12}} value={it.unit||""} placeholder="hrs" onChange={e=>setSectionTemplateForm(p=>({...p,items:p.items.map((x,j)=>j===ii?{...x,unit:e.target.value}:x)}))}/>
                  <input className="form-input" style={{fontSize:12}} type="number" value={it.unitCost||0} onChange={e=>setSectionTemplateForm(p=>({...p,items:p.items.map((x,j)=>j===ii?{...x,unitCost:+e.target.value||0}:x)}))} onFocus={e=>e.target.select()}/>
                  <input className="form-input" style={{fontSize:12}} type="number" value={it.unitPrice||0} onChange={e=>setSectionTemplateForm(p=>({...p,items:p.items.map((x,j)=>j===ii?{...x,unitPrice:+e.target.value||0}:x)}))} onFocus={e=>e.target.select()}/>
                  <button className="btn-icon danger" title={t.tooltips.deleteItem} onClick={()=>setSectionTemplateForm(p=>({...p,items:p.items.filter((_,j)=>j!==ii)}))}><Icon name="trash" size={12}/></button>
                </div>
              ))}
              {(sectionTemplateForm.items||[]).length===0&&(
                <div style={{padding:"16px 8px",fontSize:12,color:"var(--text-3)",textAlign:"center"}}>No items — click "+ Add Item" to start</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>tryClose(()=>setModal("templateManager"),saveSectionTemplateForm)}>{t.estimates.cancel}</button>
              <button className="btn btn-primary" disabled={!sectionTemplateForm.title?.trim()} onClick={saveSectionTemplateForm}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TEMPLATE ADD/EDIT ── */}
      {modal==="templateEdit"&&templateForm&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&tryClose(()=>setModal("templateManager"),saveTemplateFormFn)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div className="modal-title">{templateForm.id?"Edit Template":"New Template"}</div>
                <div className="modal-sub">Changes are saved to Firestore</div>
              </div>
              <button className="btn-icon" title={t.common.close} onClick={()=>tryClose(()=>setModal("templateManager"),saveTemplateFormFn)}><Icon name="x"/></button>
            </div>
            <div className="modal-body" onChange={()=>setIsDirty(true)}>
              {/* Header fields */}
              <div className="form-grid form-grid-2" style={{marginBottom:16}}>
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label>Template Name *</label>
                  <input className="form-input" value={templateForm.name||""} onChange={e=>setTemplateForm(p=>({...p,name:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>{t.estimates.serviceType}</label>
                  <select className="form-select" value={templateForm.service||""} onChange={e=>setTemplateForm(p=>({...p,service:e.target.value}))}>
                    {["Hardscape","Landscape Design","Excavation","Pool","Commercial","Other"].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input className="form-input" value={templateForm.desc||""} onChange={e=>setTemplateForm(p=>({...p,desc:e.target.value}))} placeholder="Short description..."/>
                </div>
              </div>

              <div className="divider"/>

              {/* Sections */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:14}}>{t.estimates.lineItems}</div>
                <button className="btn btn-secondary btn-sm" onClick={()=>setTemplateForm(p=>({...p,sections:[...(p.sections||[]),{id:`s${Date.now()}`,title:"New Section",items:[]}]}))}>
                  <Icon name="plus" size={12}/>{t.estimates.addTitle2}
                </button>
              </div>

              {(templateForm.sections||[]).map((sec,si)=>(
                <div
                  key={sec.id||si}
                  className="est-section"
                  style={{
                    marginBottom:8,
                    opacity: tmplDragSec===si ? 0.5 : 1,
                    border: tmplOverSec===si && tmplDragSec!==si ? "2px dashed var(--red)" : "",
                    transition:"opacity .15s",
                  }}
                  draggable
                  onDragStart={e=>{e.stopPropagation();setTmplDragSec(si);}}
                  onDragOver={e=>{e.preventDefault();e.stopPropagation();setTmplOverSec(si);}}
                  onDrop={e=>{
                    e.preventDefault();e.stopPropagation();
                    if(tmplDragSec===null||tmplDragSec===si){setTmplDragSec(null);setTmplOverSec(null);return;}
                    setTemplateForm(p=>{
                      const secs=[...p.sections];
                      const[moved]=secs.splice(tmplDragSec,1);
                      secs.splice(si,0,moved);
                      return{...p,sections:secs};
                    });
                    setTmplDragSec(null);setTmplOverSec(null);
                  }}
                  onDragEnd={()=>{setTmplDragSec(null);setTmplOverSec(null);}}
                >
                  {/* Section header */}
                  <div className="est-section-header" style={{cursor:"default"}}>
                    <span style={{cursor:"grab",color:"var(--text-3)",display:"flex",alignItems:"center"}}>
                      <Icon name="grip" size={14}/>
                    </span>
                    <input
                      type="number" min={1} max={(templateForm.sections||[]).length}
                      value={si+1}
                      onChange={e=>{
                        const n=Math.max(1,Math.min((templateForm.sections||[]).length,+e.target.value||1))-1;
                        setTemplateForm(p=>{
                          const secs=[...p.sections];
                          const[moved]=secs.splice(si,1);
                          secs.splice(n,0,moved);
                          return{...p,sections:secs};
                        });
                      }}
                      style={{width:36,padding:"2px 4px",fontSize:11,border:"1px solid var(--border)",borderRadius:4,textAlign:"center",background:"var(--bg)",color:"var(--text-2)",fontWeight:600}}
                    />
                    <input
                      className="form-input"
                      style={{flex:1,border:"none",background:"transparent",fontWeight:600,padding:"0 4px",fontSize:13}}
                      value={sec.title||""}
                      onChange={e=>setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i===si?{...s,title:e.target.value}:s)}))}
                    />
                    <button className="btn btn-secondary btn-sm" onClick={()=>setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i===si?{...s,items:[...(s.items||[]),{id:`i${Date.now()}`,name:"",category:"Labor",qty:1,unit:"hrs",unitCost:0,unitPrice:0}]}:s)}))}>
                      <Icon name="plus" size={11}/> Item
                    </button>
                    <button className="btn-icon danger" title={t.tooltips.deleteSection} onClick={()=>setTemplateForm(p=>({...p,sections:p.sections.filter((_,i)=>i!==si)}))}>
                      <Icon name="trash" size={13}/>
                    </button>
                  </div>

                  {/* Column headers */}
                  {(sec.items||[]).length>0&&(
                    <div style={{display:"grid",gridTemplateColumns:"28px 32px 1fr 90px 80px 90px 90px 28px",gap:6,padding:"5px 14px",background:"var(--bg)",borderBottom:"1px solid var(--border)"}}>
                      {["","#","Item","Category","Qty","Unit Cost","Unit Price",""].map((h,i)=>(
                        <span key={i} style={{fontSize:10,fontWeight:600,letterSpacing:".06em",textTransform:"uppercase",color:"var(--text-3)"}}>{h}</span>
                      ))}
                    </div>
                  )}

                  {/* Items */}
                  {(sec.items||[]).map((it,ii)=>(
                    <div
                      key={it.id||ii}
                      draggable
                      onDragStart={e=>{e.stopPropagation();setTmplDragItem({si,ii});}}
                      onDragOver={e=>{e.preventDefault();e.stopPropagation();setTmplOverItem({si,ii});}}
                      onDrop={e=>{
                        e.preventDefault();e.stopPropagation();
                        if(!tmplDragItem){setTmplDragItem(null);setTmplOverItem(null);return;}
                        const{si:fromSi,ii:fromIi}=tmplDragItem;
                        setTemplateForm(p=>{
                          const secs=p.sections.map(s=>({...s,items:[...s.items]}));
                          const fromSec=secs[fromSi]; const toSec=secs[si];
                          if(!fromSec||!toSec) return p;
                          const[moved]=fromSec.items.splice(fromIi,1);
                          toSec.items.splice(ii,0,moved);
                          return{...p,sections:secs};
                        });
                        setTmplDragItem(null);setTmplOverItem(null);
                      }}
                      onDragEnd={()=>{setTmplDragItem(null);setTmplOverItem(null);}}
                      style={{
                        display:"grid",
                        gridTemplateColumns:"28px 32px 1fr 90px 80px 90px 90px 28px",
                        gap:6,padding:"7px 14px",
                        borderBottom:"1px solid var(--border)",
                        alignItems:"center",
                        opacity: tmplDragItem?.si===si&&tmplDragItem?.ii===ii ? 0.4 : 1,
                        background: tmplOverItem?.si===si&&tmplOverItem?.ii===ii&&!(tmplDragItem?.si===si&&tmplDragItem?.ii===ii) ? "#fef2f2" : "transparent",
                        transition:"opacity .15s, background .1s",
                      }}
                    >
                      {/* Drag handle */}
                      <span style={{cursor:"grab",color:"var(--text-3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <Icon name="grip" size={13}/>
                      </span>
                      {/* Order number */}
                      <input
                        type="number" min={1} max={(sec.items||[]).length}
                        value={ii+1}
                        onChange={e=>{
                          const n=Math.max(1,Math.min((sec.items||[]).length,+e.target.value||1))-1;
                          setTemplateForm(p=>{
                            const secs=p.sections.map(s=>({...s,items:[...s.items]}));
                            const items=secs[si].items;
                            const[moved]=items.splice(ii,1);
                            items.splice(n,0,moved);
                            return{...p,sections:secs};
                          });
                        }}
                        style={{width:28,padding:"2px 2px",fontSize:11,border:"1px solid var(--border)",borderRadius:4,textAlign:"center",background:"var(--bg)",color:"var(--text-2)",fontWeight:600}}
                      />
                      {/* Item name with autocomplete */}
                      <div style={{position:"relative"}}>
                        <input
                          className="form-input"
                          style={{fontSize:12,width:"100%"}}
                          value={it.name||""}
                          placeholder="Item name"
                          autoComplete="off"
                          onChange={e=>{
                            setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i!==si?s:{...s,items:s.items.map((it2,j)=>j!==ii?it2:{...it2,name:e.target.value})})}));
                            openTmplAutocomplete(e, si, ii);
                          }}
                          onFocus={e=>openTmplAutocomplete(e, si, ii)}
                          onBlur={()=>setTimeout(()=>setTmplAutocomplete(p=>p?.si===si&&p?.ii===ii?null:p),200)}
                        />
                      </div>
                      <select className="form-select" style={{fontSize:11,padding:"4px 20px 4px 6px"}} value={it.category||"Labor"}
                        onChange={e=>setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i!==si?s:{...s,items:s.items.map((it2,j)=>j!==ii?it2:{...it2,category:e.target.value})})}))}>
                        {["Labor","Equipment","Material","Subcontractor"].map(c=><option key={c}>{c}</option>)}
                      </select>
                      <input className="form-input" style={{fontSize:12}} type="number" value={it.qty||1}
                        onChange={e=>setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i!==si?s:{...s,items:s.items.map((it2,j)=>j!==ii?it2:{...it2,qty:+e.target.value||1})})}))} onFocus={e=>e.target.select()}/>
                      <input className="form-input" style={{fontSize:12}} type="number" value={it.unitCost||0}
                        onChange={e=>setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i!==si?s:{...s,items:s.items.map((it2,j)=>j!==ii?it2:{...it2,unitCost:+e.target.value||0})})}))} onFocus={e=>e.target.select()}/>
                      <input className="form-input" style={{fontSize:12}} type="number" value={it.unitPrice||0}
                        onChange={e=>setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i!==si?s:{...s,items:s.items.map((it2,j)=>j!==ii?it2:{...it2,unitPrice:+e.target.value||0})})}))} onFocus={e=>e.target.select()}/>
                      <button className="btn-icon danger" title={t.tooltips.deleteItem} onClick={()=>setTemplateForm(p=>({...p,sections:p.sections.map((s,i)=>i!==si?s:{...s,items:s.items.filter((_,j)=>j!==ii)})}))}>
                        <Icon name="trash" size={12}/>
                      </button>
                    </div>
                  ))}

                  {(sec.items||[]).length===0&&(
                    <div style={{padding:"12px 14px",fontSize:12,color:"var(--text-3)",textAlign:"center"}}>
                      No items — click "+ Item" to add
                    </div>
                  )}
                </div>
              ))}

              {(templateForm.sections||[]).length===0&&(
                <div className="empty-state" style={{padding:"24px"}}>
                  <div className="empty-icon"><Icon name="estimates" size={28}/></div>
                  <div className="empty-sub">No sections yet — click "Add Title" to start</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>tryClose(()=>setModal("templateManager"),saveTemplateFormFn)}>{t.estimates.cancel}</button>
              <button className="btn btn-primary" onClick={saveTemplateFormFn}>Save Template</button>
            </div>
          </div>
        </div>
      )}

      {modal==="edit"&&form&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&tryClose(()=>setModal(null),saveEst)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div><div style={{display:"flex",alignItems:"center",gap:10}}><div className="modal-title">{form.id?t.estimates.editTitle:t.estimates.addTitle}</div><button className="btn btn-secondary btn-sm" onClick={openTemplatesInEdit}><Icon name="template" size={12}/>{t.estimates.useTemplate}</button></div><div className="modal-sub">Budget Estimate Creation</div></div>
              <button className="btn-icon" title={t.common.close} onClick={()=>tryClose(()=>setModal(null),saveEst)}><Icon name="x"/></button>
            </div>
            <div className="modal-body" onChange={()=>setIsDirty(true)}>
              <div className="form-grid">
                <div className="form-group"><label>{t.estimates.estimateTitle} *</label><input className="form-input" value={form.title||""} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
                <div className="form-grid form-grid-4" style={{gap:10}}>
                  <div className="form-group"><label>{t.estimates.customer} *</label><select className="form-select" value={form.customerId||""} onChange={e=>setForm(p=>({...p,customerId:e.target.value}))}><option value="">—</option>{customers.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}</select></div>
                  <div className="form-group"><label>{t.estimates.serviceType}</label><select className="form-select" value={form.serviceType||""} onChange={e=>setForm(p=>({...p,serviceType:e.target.value}))}>{["Hardscape","Landscape","Excavation","Pool","Other"].map(s=><option key={s}>{s}</option>)}</select></div>
                  <div className="form-group"><label>{t.estimates.status}</label><select className="form-select" value={form.status||""} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>{[["draft","Draft"],["sent","Sent"],["revised","Revised"],["approved","Approved"],["rejected","Rejected"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></div>
                  <div className="form-group"><label>{t.estimates.validDate}</label><input className="form-input" type="date" value={form.validUntil||""} onChange={e=>setForm(p=>({...p,validUntil:e.target.value}))}/></div>
                  <div className="form-group"><label>{t.estimates.jobTypeTag}</label><select className="form-select" value={form.jobTypeTag||""} onChange={e=>setForm(p=>({...p,jobTypeTag:e.target.value}))}><option value="">—</option>{Object.entries(t.estimates.jobTypeTags).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
                </div>
              </div>
              <div className="divider"/>
              {/* ── Tab bar ── */}
              <div style={{display:"flex",gap:2,marginBottom:16,borderBottom:"1px solid var(--border)",paddingBottom:0}}>
                <button onClick={()=>setEstEditTab("items")} style={{padding:"7px 14px",fontSize:13,fontWeight:600,background:"none",border:"none",cursor:"pointer",borderBottom:estEditTab==="items"?"2px solid var(--red)":"2px solid transparent",color:estEditTab==="items"?"var(--red)":"var(--text-2)",marginBottom:-1,fontFamily:"inherit"}}>
                  {t.estimates.lineItems}
                </button>
                <button onClick={()=>setEstEditTab("materials")} style={{padding:"7px 14px",fontSize:13,fontWeight:600,background:"none",border:"none",cursor:"pointer",borderBottom:estEditTab==="materials"?"2px solid var(--red)":"2px solid transparent",color:estEditTab==="materials"?"var(--red)":"var(--text-2)",marginBottom:-1,fontFamily:"inherit",display:"flex",alignItems:"center",gap:6}}>
                  {t.estimates.materialsTab}
                  {(form.referencePhotos||[]).length>0&&<span style={{background:"var(--red)",color:"white",borderRadius:8,fontSize:10,fontWeight:700,padding:"1px 5px"}}>{(form.referencePhotos||[]).length}</span>}
                </button>
                <button onClick={()=>setEstEditTab("plans")} style={{padding:"7px 14px",fontSize:13,fontWeight:600,background:"none",border:"none",cursor:"pointer",borderBottom:estEditTab==="plans"?"2px solid var(--red)":"2px solid transparent",color:estEditTab==="plans"?"var(--red)":"var(--text-2)",marginBottom:-1,fontFamily:"inherit",display:"flex",alignItems:"center",gap:6}}>
                  {t.estimates.designPlansTab}
                  {(form.designPlans||[]).length>0&&<span style={{background:"var(--red)",color:"white",borderRadius:8,fontSize:10,fontWeight:700,padding:"1px 5px"}}>{(form.designPlans||[]).length}</span>}
                </button>
                {role==="admin"&&form.id&&(
                  <button onClick={()=>{setEstEditTab("versions");if(!versionsLoaded)loadVersions(form.id);}} style={{padding:"7px 14px",fontSize:13,fontWeight:600,background:"none",border:"none",cursor:"pointer",borderBottom:estEditTab==="versions"?"2px solid var(--red)":"2px solid transparent",color:estEditTab==="versions"?"var(--red)":"var(--text-2)",marginBottom:-1,fontFamily:"inherit",display:"flex",alignItems:"center",gap:6}}>
                    {t.estimates.versionsTab}
                    {versions.length>0&&<span style={{background:"var(--red)",color:"white",borderRadius:8,fontSize:10,fontWeight:700,padding:"1px 5px"}}>{versions.length}</span>}
                  </button>
                )}
              </div>
              {estEditTab==="items"&&(
              <>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:14}}>{t.estimates.lineItems}</div>
                <div style={{display:"flex",gap:6}}>
                  <button className="btn btn-secondary btn-sm" onClick={addSection}><Icon name="plus" size={12}/>{t.estimates.addTitle2}</button>
                </div>
              </div>
              {form.sections.filter(sec=>sec.type!=="group").map((sec, secIdx)=>{
                const realSections = form.sections.filter(s=>s.type!=="group");
                return (
                <div
                  key={sec.id}
                  className="est-section"
                  onDragOver={e=>onSectionDragOver(e,sec.id)}
                  onDrop={e=>onSectionDrop(e,sec.id)}
                  onDragEnd={()=>{setDragSection(null);setOverSection(null);}}
                  style={{
                    opacity: dragSection===sec.id ? 0.5 : 1,
                    border: overSection===sec.id && dragSection!==sec.id ? "2px dashed var(--red)" : "",
                    transition: "opacity .15s",
                  }}
                >
                  <div className="est-section-header" onClick={()=>toggleSection(sec.id)}>
                    <span draggable onDragStart={e=>onSectionDragStart(e,sec.id)} style={{cursor:"grab",color:"var(--text-3)",display:"flex",alignItems:"center"}} onMouseDown={e=>e.stopPropagation()}>
                      <Icon name="grip" size={14}/>
                    </span>
                    <input
                      type="number"
                      min={1} max={realSections.length}
                      value={secIdx+1}
                      onClick={e=>e.stopPropagation()}
                      onChange={e=>reorderSectionByNum(sec.id, e.target.value)}
                      style={{width:36,padding:"2px 4px",fontSize:11,border:"1px solid var(--border)",borderRadius:4,textAlign:"center",background:"var(--bg)",color:"var(--text-2)",fontWeight:600}}
                    />
                    <button className="btn-icon est-collapse-btn" title={expandedSections[sec.id]?t.tooltips.collapseSection:t.tooltips.expandSection} onClick={e=>{e.stopPropagation();toggleSection(sec.id);}}>
                      <Icon name={expandedSections[sec.id]?"chevronRight":"chevronDown"} size={13}/>
                    </button>
                    <input
                      className="form-input"
                      style={{flex:1,border:"none",background:"transparent",fontWeight:600,padding:"0 4px",fontSize:13}}
                      value={sec.title}
                      onClick={e=>e.stopPropagation()}
                      onFocus={e=>{e.stopPropagation();e.target.select();}}
                      onChange={e=>updateSection(sec.id,"title",e.target.value)}
                    />
                    <span className="est-section-total">{fmt(sectionTotal(sec))}</span>
                    {/* + Ajouter dropdown */}
                    <div style={{position:"relative"}} onClick={e=>e.stopPropagation()}>
                      <button className="btn-icon" title={t.tooltips.addItem} onClick={e=>{e.stopPropagation();setAddDropdown(p=>p===sec.id?null:sec.id);}}>
                        <Icon name="plus" size={13}/>
                      </button>
                      {addDropdown===sec.id&&(
                        <div style={{position:"absolute",right:0,top:"100%",zIndex:1000,background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,boxShadow:"0 4px 16px rgba(0,0,0,.12)",minWidth:190,padding:"4px 0"}}>
                          <button style={{display:"block",width:"100%",textAlign:"left",padding:"7px 14px",fontSize:12,background:"none",border:"none",cursor:"pointer",color:"var(--text)"}} onMouseDown={e=>{e.preventDefault();addItem(sec.id);setAddDropdown(null);}}>
                            + Élément / Item
                          </button>
                          <button style={{display:"block",width:"100%",textAlign:"left",padding:"7px 14px",fontSize:12,background:"none",border:"none",cursor:"pointer",color:"var(--text)"}} onMouseDown={e=>{e.preventDefault();addSubheading(sec.id);setAddDropdown(null);}}>
                            + Sous-titre / Sub-heading
                          </button>
                        </div>
                      )}
                    </div>
                    <button className="btn-icon" title={t.estimates.saveSectionBtn} onClick={e=>{e.stopPropagation();setSaveSectionForm({name:sec.title||"",category:"Other",description:""});setSaveSectionModal({sec});}}><Icon name="bookmark" size={13}/></button>
                    <button className="btn-icon danger" title={t.tooltips.deleteSection} onClick={e=>{e.stopPropagation();setInnerConfirm({type:"section",sid:sec.id,label:sec.title||"cette section"});}}><Icon name="trash" size={13}/></button>
                  </div>

                  {!expandedSections[sec.id]&&(
                    <>
                      <textarea
                        value={sec.description||""}
                        onChange={e=>updateSection(sec.id,"description",e.target.value)}
                        onClick={e=>e.stopPropagation()}
                        placeholder="Description (optionnelle)"
                        rows={2}
                        style={{display:"block",width:"100%",boxSizing:"border-box",padding:"8px 14px",fontSize:12,color:"var(--text-2)",background:"var(--bg-2)",border:"none",borderBottom:"1px solid var(--border)",resize:"vertical",fontFamily:"inherit",outline:"none"}}
                      />
                      {sec.items.filter(it=>it.type!=="subheading").length>0&&(
                        <div className="est-col-header" style={{gridTemplateColumns:"28px 32px 1fr 90px 90px 90px 90px 100px 28px 28px 28px"}}>
                          <span/>
                          <span className="est-col-label">#</span>
                          <span className="est-col-label">{t.estimates.addItem}</span>
                          <span className="est-col-label">{t.estimates.category}</span>
                          <span className="est-col-label">{t.estimates.qty}</span>
                          <span className="est-col-label">{t.estimates.unitCost}</span>
                          <span className="est-col-label">{t.estimates.unitPrice}</span>
                          <span className="est-col-label">{t.estimates.lineTotal}</span>
                          <span/>
                          <span/>
                          <span/>
                        </div>
                      )}
                      {sec.items.map((it, itIdx)=>{
                        if(it.type==="subheading") return (
                          <div
                            key={it.id}
                            onDragOver={e=>onItemDragOver(e,sec.id,it.id)}
                            onDrop={e=>onItemDrop(e,sec.id,it.id)}
                            onDragEnd={()=>{setDragItem(null);setOverItem(null);}}
                            style={{
                              display:"flex",alignItems:"center",gap:6,
                              padding:"5px 14px",
                              borderTop:"1px solid var(--border)",
                              borderBottom:"1px solid var(--border)",
                              background:"var(--bg-2)",
                              opacity: dragItem?.itemId===it.id ? 0.4 : 1,
                              transition:"opacity .15s",
                            }}
                          >
                            <span draggable onDragStart={e=>onItemDragStart(e,sec.id,it.id)} style={{cursor:"grab",color:"var(--text-3)",display:"flex",alignItems:"center"}} onMouseDown={e=>e.stopPropagation()}>
                              <Icon name="grip" size={12}/>
                            </span>
                            <input
                              style={{flex:1,border:"none",background:"transparent",outline:"none",fontSize:11,fontWeight:600,letterSpacing:".06em",textTransform:"uppercase",color:"var(--text-2)",fontFamily:"inherit"}}
                              value={it.label||""}
                              placeholder="Sous-titre…"
                              onChange={e=>setForm(p=>({...p,sections:p.sections.map(s=>s.id===sec.id?{...s,items:s.items.map(i=>i.id===it.id?{...i,label:e.target.value}:i)}:s)}))}
                            />
                            <button className="btn-icon" title="Ajouter un élément / Add item" style={{opacity:.35,transition:"opacity .15s"}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.35} onClick={()=>insertItemAfter(sec.id,it.id)}><Icon name="plus" size={12}/></button>
                            <button className="btn-icon danger" onClick={()=>removeItem(sec.id,it.id)}><Icon name="trash" size={12}/></button>
                          </div>
                        );
                        const realIdx = sec.items.slice(0,itIdx).filter(i=>i.type!=="subheading").length;
                        return (
                        <div
                          key={it.id}
                          onDragOver={e=>onItemDragOver(e,sec.id,it.id)}
                          onDrop={e=>onItemDrop(e,sec.id,it.id)}
                          onDragEnd={()=>{setDragItem(null);setOverItem(null);}}
                          style={{
                            display:"grid",
                            gridTemplateColumns:"28px 32px 1fr 90px 90px 90px 90px 100px 28px 28px 28px",
                            gap:6, padding:"8px 14px",
                            borderBottom:"1px solid var(--border)",
                            alignItems:"center",
                            overflow:"visible",
                            opacity: dragItem?.itemId===it.id ? 0.4 : 1,
                            background: overItem?.itemId===it.id && dragItem?.itemId!==it.id ? "#fef2f2" : "transparent",
                            transition:"opacity .15s, background .1s",
                          }}
                        >
                          <span draggable onDragStart={e=>onItemDragStart(e,sec.id,it.id)} style={{cursor:"grab",color:"var(--text-3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            <Icon name="grip" size={13}/>
                          </span>
                          <input
                            type="number"
                            min={1} max={sec.items.filter(i=>i.type!=="subheading").length}
                            value={realIdx+1}
                            onChange={e=>reorderItemByNum(sec.id,it.id,e.target.value)}
                            style={{width:28,padding:"2px 2px",fontSize:11,border:"1px solid var(--border)",borderRadius:4,textAlign:"center",background:"var(--bg)",color:"var(--text-2)",fontWeight:600}}
                          />
                          <div style={{position:"relative"}}>
                            <input
                              className="form-input"
                              style={{fontSize:12,width:"100%"}}
                              value={it.name}
                              placeholder="Item name"
                              autoComplete="off"
                              onMouseDown={e=>e.stopPropagation()}
                              onChange={e=>{
                                updateItem(sec.id,it.id,"name",e.target.value);
                                openAutocomplete(e, sec.id, it.id);
                              }}
                              onFocus={e=>openAutocomplete(e, sec.id, it.id)}
                              onBlur={()=>setTimeout(()=>setAutocomplete(p=>p?.secId===sec.id&&p?.itemId===it.id?null:p),200)}
                            />
                          </div>
                          <select className="form-select" style={{fontSize:11,padding:"4px 20px 4px 6px"}} value={it.category} onChange={e=>updateItem(sec.id,it.id,"category",e.target.value)}>
                            {["Labor","Equipment","Material","Subcontractor"].map(c=><option key={c}>{c}</option>)}
                          </select>
                          <input className="form-input" style={{fontSize:12}} type="number" value={it.qty} onChange={e=>updateItem(sec.id,it.id,"qty",e.target.value)} onFocus={e=>e.target.select()}/>
                          <input className="form-input" style={{fontSize:12}} type="number" value={it.unitCost} onChange={e=>updateItem(sec.id,it.id,"unitCost",e.target.value)} onFocus={e=>e.target.select()}/>
                          <input className="form-input" style={{fontSize:12}} type="number" value={it.unitPrice} onChange={e=>updateItem(sec.id,it.id,"unitPrice",e.target.value)} onFocus={e=>e.target.select()}/>
                          <span style={{fontSize:13,fontWeight:500}}>{fmt((it.unitPrice||0)*(it.qty||1))}</span>
                          <button className="btn-icon" title="Ajouter un élément / Add item" style={{opacity:.35,transition:"opacity .15s"}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.35} onClick={()=>insertItemAfter(sec.id,it.id)}><Icon name="plus" size={12}/></button>
                          <button className="btn-icon" title="Sauvegarder au prix / Save to Price List" style={{opacity: plFlash===it.id ? 1 : .35, color: plFlash===it.id ? "#16a34a" : undefined, transition:"opacity .15s, color .15s"}} onMouseEnter={e=>{if(plFlash!==it.id)e.currentTarget.style.opacity=1;}} onMouseLeave={e=>{if(plFlash!==it.id)e.currentTarget.style.opacity=.35;}} onClick={()=>openPlModal(it)}><Icon name="bookmark" size={12}/></button>
                          <button className="btn-icon danger" title={t.tooltips.deleteItem} onClick={()=>setInnerConfirm({type:"item",sid:sec.id,iid:it.id,label:it.name||"cet item"})}><Icon name="trash" size={12}/></button>
                        </div>
                        );
                      })}
                    </>
                  )}
                </div>
                );
              })}
              {form.sections.length>0&&(()=>{
                const sub = estSubtotal(form);
                const tps = Math.round(sub*0.05*100)/100;
                const tvq = Math.round(sub*0.09975*100)/100;
                const total = sub+tps+tvq;
                return (
                  <div className="est-totals">
                    <div className="est-totals-box">
                      {[{l:t.estimates.subtotal,v:fmt(sub)},{l:"TPS (5%)",v:fmt(tps)},{l:"TVQ (9,975%)",v:fmt(tvq)}].map((r,i)=><div key={i} className="est-total-row"><span>{r.l}</span><span>{r.v}</span></div>)}
                      <div className="est-total-row grand"><span>{t.estimates.total}</span><span>{fmt(total)}</span></div>
                    </div>
                  </div>
                );
              })()}
              </>
              )}
              {estEditTab==="materials"&&(()=>{
                const MAT_CATS = ["all","Material","Equipment","Other"];
                const libPhotos = (materialPhotos||[]).filter(p=>{
                  const matchSearch = !matSearch || p.name.toLowerCase().includes(matSearch.toLowerCase());
                  const matchCat = matCatFilter==="all" || p.category===matCatFilter;
                  return matchSearch && matchCat;
                });
                const addedPhotos = form.referencePhotos||[];
                const isAdded = (mp) => addedPhotos.some(a=>a.materialPhotoId===mp.id);
                const addRef = (mp) => {
                  if(isAdded(mp)) return;
                  setForm(p=>({...p,referencePhotos:[...(p.referencePhotos||[]),{materialPhotoId:mp.id,name:mp.name,caption:mp.caption||"",photoURL:mp.photoURL}]}));
                  setIsDirty(true);
                };
                const removeRef = (mpId) => {
                  setForm(p=>({...p,referencePhotos:(p.referencePhotos||[]).filter(a=>a.materialPhotoId!==mpId)}));
                  setIsDirty(true);
                };
                const updateCaption = (mpId, caption) => {
                  setForm(p=>({...p,referencePhotos:(p.referencePhotos||[]).map(a=>a.materialPhotoId===mpId?{...a,caption}:a)}));
                  setIsDirty(true);
                };
                return (
                  <div>
                    {/* Library grid */}
                    <div style={{marginBottom:16}}>
                      <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:13,marginBottom:10}}>{t.settings.materialLibrary}</div>
                      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                        <input className="form-input" style={{flex:"1 1 180px",fontSize:12}} placeholder={t.common?.loading?"":"Rechercher..."} value={matSearch} onChange={e=>setMatSearch(e.target.value)}/>
                        {MAT_CATS.map(c=>(
                          <button key={c} onClick={()=>setMatCatFilter(c)} style={{padding:"5px 12px",fontSize:11,fontWeight:600,borderRadius:20,border:"1px solid var(--border)",background:matCatFilter===c?"var(--red)":"transparent",color:matCatFilter===c?"white":"var(--text-2)",cursor:"pointer",fontFamily:"inherit"}}>
                            {c==="all"?(t.estimates.templateAll||"All"):c}
                          </button>
                        ))}
                      </div>
                      {libPhotos.length===0?(
                        <div style={{fontSize:13,color:"var(--text-3)",padding:"20px 0",textAlign:"center"}}>{t.estimates.noMaterialPhotos}</div>
                      ):(
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10}}>
                          {libPhotos.map(mp=>{
                            const added = isAdded(mp);
                            return (
                              <div key={mp.id} style={{border:`1px solid ${added?"var(--red)":"var(--border)"}`,borderRadius:8,overflow:"hidden",position:"relative",background:added?"#fef2f2":"var(--surface)"}}>
                                <img src={mp.photoURL} alt={mp.name} style={{width:"100%",aspectRatio:"4/3",objectFit:"cover",display:"block"}}/>
                                <div style={{padding:"6px 8px"}}>
                                  <div style={{fontWeight:600,fontSize:12,marginBottom:1}}>{mp.name}</div>
                                  {mp.caption&&<div style={{fontSize:10,color:"var(--text-3)",marginBottom:4}}>{mp.caption}</div>}
                                  <span style={{fontSize:9,fontWeight:600,color:"var(--text-3)",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:4,padding:"1px 5px"}}>{mp.category}</span>
                                </div>
                                <button
                                  onClick={()=>added?removeRef(mp.id):addRef(mp)}
                                  style={{display:"block",width:"100%",padding:"5px",fontSize:11,fontWeight:700,background:added?"var(--red)":"var(--surface)",color:added?"white":"var(--red)",border:"none",borderTop:"1px solid var(--border)",cursor:"pointer",fontFamily:"inherit"}}
                                >
                                  {added?("✓ "+t.estimates.addedToEstimate):t.estimates.addToEstimate}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* Added photos */}
                    {addedPhotos.length>0&&(
                      <div style={{borderTop:"1px solid var(--border)",paddingTop:14}}>
                        <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:13,marginBottom:10}}>{t.estimates.addedMaterials} ({addedPhotos.length})</div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
                          {addedPhotos.map(ap=>(
                            <div key={ap.materialPhotoId} style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                              <img src={ap.photoURL} alt={ap.name} style={{width:"100%",aspectRatio:"4/3",objectFit:"cover",display:"block"}}/>
                              <div style={{padding:"6px 8px"}}>
                                <div style={{fontWeight:600,fontSize:12,marginBottom:4}}>{ap.name}</div>
                                {editingCaption===ap.materialPhotoId?(
                                  <div style={{display:"flex",gap:4}}>
                                    <input
                                      className="form-input"
                                      style={{fontSize:11,flex:1,padding:"3px 6px"}}
                                      autoFocus
                                      value={captionDraft}
                                      onChange={e=>setCaptionDraft(e.target.value)}
                                      onKeyDown={e=>{if(e.key==="Enter"){updateCaption(ap.materialPhotoId,captionDraft);setEditingCaption(null);}if(e.key==="Escape")setEditingCaption(null);}}
                                    />
                                    <button className="btn-icon" onClick={()=>{updateCaption(ap.materialPhotoId,captionDraft);setEditingCaption(null);}}><Icon name="check" size={11}/></button>
                                  </div>
                                ):(
                                  <div style={{fontSize:11,color:"var(--text-3)",minHeight:16,cursor:"pointer"}} onClick={()=>{setEditingCaption(ap.materialPhotoId);setCaptionDraft(ap.caption||"");}}>
                                    {ap.caption||<span style={{fontStyle:"italic",opacity:.5}}>{t.settings.materialCaption}…</span>}
                                  </div>
                                )}
                              </div>
                              <button onClick={()=>removeRef(ap.materialPhotoId)} style={{display:"block",width:"100%",padding:"5px",fontSize:11,fontWeight:600,background:"transparent",color:"var(--red)",border:"none",borderTop:"1px solid var(--border)",cursor:"pointer",fontFamily:"inherit"}}>
                                {t.estimates.removeMaterial||"Retirer"}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {estEditTab==="plans"&&(()=>{
                const plans = form.designPlans||[];
                const handleDpUpload = async (file) => {
                  if (!file) return;
                  if (file.size > 10*1024*1024) { alert("Fichier trop volumineux (max 10 MB)"); return; }
                  setDpUploading(true);
                  try {
                    const ts = Date.now();
                    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g,"_");
                    const path = `photos/designPlans/${form.id}/${ts}_${safe}`;
                    const sRef = storageRef(storage, path);
                    await uploadBytes(sRef, file);
                    const photoURL = await getDownloadURL(sRef);
                    const id = Math.random().toString(36).slice(2,10);
                    const name = file.name.replace(/\.[^/.]+$/,"");
                    setForm(p=>({...p,designPlans:[...(p.designPlans||[]),{id,name,caption:"",photoURL,storagePath:path,uploadedAt:new Date().toISOString()}]}));
                    setIsDirty(true);
                  } catch(err){ console.error("Design plan upload error:",err); }
                  finally { setDpUploading(false); }
                };
                const updateDpCaption = (id,caption) => {
                  setForm(p=>({...p,designPlans:(p.designPlans||[]).map(d=>d.id===id?{...d,caption}:d)}));
                  setIsDirty(true);
                };
                return (
                  <div>
                    <div style={{marginBottom:16}}>
                      {!form.id ? (
                        <div style={{fontSize:13,color:"var(--text-3)",padding:"20px 0",textAlign:"center",fontStyle:"italic"}}>Sauvegardez d'abord la soumission pour ajouter des plans.</div>
                      ) : (
                        <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",background:"var(--red)",color:"white",borderRadius:6,cursor:dpUploading?"wait":"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",opacity:dpUploading?0.65:1}}>
                          <input type="file" accept="image/*" style={{display:"none"}} disabled={dpUploading} onChange={async e=>{const f=e.target.files?.[0];e.target.value="";if(f)await handleDpUpload(f);}}/>
                          {dpUploading?"...":`+ ${t.estimates.addDesignPlan}`}
                        </label>
                      )}
                    </div>
                    {plans.map(plan=>(
                      <div key={plan.id} style={{marginBottom:16,border:"1px solid var(--border)",borderRadius:8,overflow:"hidden",background:"var(--surface)"}}>
                        <img src={plan.photoURL} alt={plan.name} style={{width:"100%",display:"block",maxHeight:420,objectFit:"contain",background:"#f8f7f5"}}/>
                        <div style={{padding:"8px 10px",display:"flex",alignItems:"center",gap:8,borderTop:"1px solid var(--border)"}}>
                          {dpEditCaption===plan.id?(
                            <div style={{display:"flex",gap:4,flex:1}}>
                              <input className="form-input" style={{fontSize:12,flex:1,padding:"3px 6px"}} autoFocus value={dpCaptionDraft} onChange={e=>setDpCaptionDraft(e.target.value)}
                                onKeyDown={e=>{if(e.key==="Enter"){updateDpCaption(plan.id,dpCaptionDraft);setDpEditCaption(null);}if(e.key==="Escape")setDpEditCaption(null);}}/>
                              <button className="btn-icon" onClick={()=>{updateDpCaption(plan.id,dpCaptionDraft);setDpEditCaption(null);}}><Icon name="check" size={11}/></button>
                            </div>
                          ):(
                            <div style={{flex:1,fontSize:12,color:"var(--text-2)",cursor:"pointer",minHeight:20}} onClick={()=>{setDpEditCaption(plan.id);setDpCaptionDraft(plan.caption||"");}}>
                              {plan.caption||<span style={{fontStyle:"italic",opacity:.5}}>{t.estimates.designPlanCaption}…</span>}
                            </div>
                          )}
                          <button className="btn-icon" title="Supprimer" onClick={()=>setDpConfirm(plan)} style={{color:"var(--red)",flexShrink:0}}><Icon name="trash" size={14}/></button>
                        </div>
                      </div>
                    ))}
                    {dpConfirm&&(
                      <ConfirmDelete
                        message={`Supprimer le plan "${dpConfirm.name}"?`}
                        onConfirm={async()=>{
                          try{await deleteObject(storageRef(storage,dpConfirm.storagePath));}catch(_){}
                          setForm(p=>({...p,designPlans:(p.designPlans||[]).filter(d=>d.id!==dpConfirm.id)}));
                          setIsDirty(true);
                          setDpConfirm(null);
                        }}
                        onCancel={()=>setDpConfirm(null)}
                      />
                    )}
                  </div>
                );
              })()}
              {estEditTab==="versions"&&(()=>{
                if (!form.id) return <div style={{padding:"32px 0",textAlign:"center",color:"var(--text-3)",fontSize:13}}>Sauvegardez d'abord la soumission.</div>;
                if (!versionsLoaded) return <div style={{padding:"32px 0",textAlign:"center",color:"var(--text-3)",fontSize:13}}>{t.common.loading}</div>;
                if (versions.length===0) return <div style={{padding:"40px 0",textAlign:"center"}}><div style={{fontSize:28,marginBottom:8}}>📋</div><div style={{fontSize:14,color:"var(--text-3)"}}>{t.estimates.noVersions}</div></div>;
                return (
                  <div>
                    {versions.map(ver=>{
                      const isOnly = versions.length === 1;
                      const isRestoreChk = (ver.label === "Avant restauration" || ver.label === "Before restore") && versions[0]?.id === ver.id;
                      const deleteDisabled = isOnly || isRestoreChk;
                      const deleteTooltip = isOnly
                        ? "Impossible de supprimer la seule version / Cannot delete the only version"
                        : isRestoreChk
                        ? "Point de contrôle de restauration — impossible à supprimer / Restore checkpoint — cannot be deleted"
                        : "Supprimer cette version / Delete this version";
                      const isRenaming = versionRenaming?.verId === ver.id;
                      return (
                        <div key={ver.id} className="ver-card">
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2}}>
                            <div className="ver-card-num">Version {ver.versionNumber}</div>
                            {role==="admin"&&(
                              <div style={{display:"flex",gap:2}}>
                                <button className="btn-icon" title="Renommer / Rename" style={{opacity:isRenaming?0.3:1}} disabled={isRenaming} onClick={()=>{setVersionRenaming({verId:ver.id});setVersionRenameDraft(ver.label||"");}}><Icon name="edit" size={13}/></button>
                                <button className="btn-icon danger" title={deleteTooltip} disabled={deleteDisabled} style={{opacity:deleteDisabled?0.3:1}} onClick={()=>!deleteDisabled&&setVersionDeleteConfirm({ver})}><Icon name="trash" size={13}/></button>
                              </div>
                            )}
                          </div>
                          {isRenaming ? (
                            <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6}}>
                              <input
                                className="form-input"
                                style={{flex:1,fontSize:13,padding:"4px 8px"}}
                                value={versionRenameDraft}
                                autoFocus
                                onChange={e=>setVersionRenameDraft(e.target.value)}
                                onKeyDown={e=>{
                                  if(e.key==="Enter"){if(versionRenameDraft.trim())doRenameVersion(ver.id,versionRenameDraft);else{setVersionRenaming(null);setVersionRenameDraft("");}}
                                  if(e.key==="Escape"){setVersionRenaming(null);setVersionRenameDraft("");}
                                }}
                              />
                              <button className="btn-icon" title="Confirmer / Confirm" onClick={()=>{if(versionRenameDraft.trim())doRenameVersion(ver.id,versionRenameDraft);else{setVersionRenaming(null);setVersionRenameDraft("");}}}><Icon name="check" size={13}/></button>
                              <button className="btn-icon" title={t.common.close} onClick={()=>{setVersionRenaming(null);setVersionRenameDraft("");}}><Icon name="x" size={13}/></button>
                            </div>
                          ) : (
                            ver.label ? <div className="ver-card-label">{ver.label}</div> : <div className="ver-card-label-empty">{t.estimates.versionUnlabelled}</div>
                          )}
                          <div className="ver-card-meta">
                            <span>{fmtVerDate(ver.savedAt)}</span>
                            {ver.savedBy&&<span>{ver.savedBy}</span>}
                            <span>{fmt(ver.totalWithTax)}</span>
                            <span>{ver.sectionCount} section{ver.sectionCount!==1?"s":""}</span>
                          </div>
                          <div className="ver-card-actions">
                            <button className="btn btn-secondary btn-sm" onClick={()=>setVersionPreview(ver)}>{t.estimates.versionPreviewBtn}</button>
                            <button className="btn btn-secondary btn-sm" onClick={()=>setVersionRestoreConfirm(ver)}>{t.estimates.versionRestoreBtn}</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            {role==="admin"&&form.id&&versionLabelPrompt&&(
              <div className="ver-label-prompt">
                <input
                  className="form-input"
                  style={{flex:1,fontSize:13}}
                  placeholder={t.estimates.versionLabelPlaceholder}
                  value={versionLabel}
                  onChange={e=>setVersionLabel(e.target.value)}
                  autoFocus
                  onKeyDown={e=>{if(e.key==="Enter")doSaveVersion();if(e.key==="Escape"){setVersionLabelPrompt(false);setVersionLabel("");}}}
                />
                <button className="btn btn-primary btn-sm" onClick={doSaveVersion} disabled={versionSaving}>{versionSaving?"...":"Confirmer"}</button>
                <button className="btn btn-secondary btn-sm" onClick={()=>{setVersionLabelPrompt(false);setVersionLabel("");}}>{t.estimates.cancel}</button>
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>tryClose(()=>setModal(null),saveEst)}>{t.estimates.cancel}</button>
              {form.id&&<button className="btn btn-secondary" onClick={()=>printEstimate(form)}><Icon name="printer" size={14}/>Imprimer</button>}
              <button className="btn btn-secondary" onClick={()=>{setSaveTemplateName(form.title||"");setSaveTemplateCategory('Excavation');setSaveTemplateDone(false);setShowSaveTemplateModal(true);}}><Icon name="template" size={14}/>{t.estimates.saveAsTemplate}</button>
              {(()=>{const _ec=customers.find(c=>c.id===form?.customerId);return _ec?.email&&_ec?.portalToken?<button className="btn btn-secondary" onClick={handleSendEstimateEmail} disabled={sendingEstimateEmail} title="Envoyer par courriel"><Icon name="mail" size={14}/>{estimateEmailSent?"✓ Envoyé":sendingEstimateEmail?"...":"Envoyer"}</button>:null;})()}
              {role==="admin"&&form.id&&(
                <button className="btn btn-secondary" disabled={versionLabelPrompt} onClick={()=>setVersionLabelPrompt(true)} style={{color:versionSaved?"#16a34a":"",borderColor:versionSaved?"#16a34a":""}}>
                  {versionSaved?"✓ "+t.estimates.versionSavedFlash:t.estimates.saveVersion}
                </button>
              )}
              <button className="btn btn-primary" onClick={saveEst}>{t.estimates.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Save as Template modal */}
      {showSaveTemplateModal && (
        <div className="modal-overlay" onClick={()=>{if(!saveTemplateSaving){setShowSaveTemplateModal(false);}}}>
          <div className="modal" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><Icon name="template" size={16}/> {t.estimates.saveAsTemplateTitle}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>{if(!saveTemplateSaving)setShowSaveTemplateModal(false);}}><Icon name="x" size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t.estimates.templateNameLabel} *</label>
                <input className="form-input" value={saveTemplateName} onChange={e=>setSaveTemplateName(e.target.value)} placeholder={t.estimates.templateNamePlaceholder}/>
              </div>
              <div className="form-group">
                <label>{t.estimates.category}</label>
                <select className="form-input" value={saveTemplateCategory} onChange={e=>setSaveTemplateCategory(e.target.value)}>
                  {["Excavation","Hardscape","Landscape Design","Pool","Other"].map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>
                {(form?.sections||[]).length} {t.estimates.templateSectionBadge?.toLowerCase()||"section(s)"} · {countItems(form||{})} {t.estimates.items}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>{if(!saveTemplateSaving)setShowSaveTemplateModal(false);}}>{t.estimates.cancel}</button>
              <button className="btn btn-primary" disabled={!saveTemplateName.trim()||saveTemplateSaving} onClick={handleSaveAsTemplate}>
                {saveTemplateDone ? t.estimates.templateSaved : saveTemplateSaving ? "..." : <><Icon name="template" size={14}/>{t.estimates.saveAsTemplate}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Autocomplete dropdown — fixed position to escape all overflow containers */}
      {autocomplete && (() => {
        const activeItem = form?.sections
          ?.find(s=>s.id===autocomplete.secId)
          ?.items?.find(i=>i.id===autocomplete.itemId);
        const suggestions = getSuggestions(activeItem?.name||"");
        if (!suggestions.length) return null;
        return (
          <div style={{
            position:"fixed",
            top: autocomplete.y,
            left: autocomplete.x,
            width: autocomplete.width,
            background:"var(--card)",
            border:"1px solid var(--border)",
            borderRadius:"var(--radius-sm)",
            boxShadow:"var(--shadow-lg)",
            zIndex:99999,
            maxHeight:220,
            overflowY:"auto",
          }}>
            {suggestions.map((pl,si)=>{
              const query = (activeItem?.name||"").toLowerCase();
              const nameLower = pl.name.toLowerCase();
              const idx = nameLower.indexOf(query);
              return (
                <div
                  key={pl.id||si}
                  onMouseDown={e=>{e.preventDefault();selectSuggestion(autocomplete.secId,autocomplete.itemId,pl);}}
                  style={{
                    padding:"8px 12px",cursor:"pointer",fontSize:12.5,
                    display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,
                    borderBottom:"1px solid var(--border)",background:"var(--card)",
                  }}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--bg)"}
                  onMouseLeave={e=>e.currentTarget.style.background="var(--card)"}
                >
                  <span style={{fontWeight:500,color:"var(--text)",flex:1}}>
                    {idx>=0?(
                      <>
                        {pl.name.slice(0,idx)}
                        <span style={{color:"var(--red)",fontWeight:700}}>{pl.name.slice(idx,idx+(activeItem?.name||"").length)}</span>
                        {pl.name.slice(idx+(activeItem?.name||"").length)}
                      </>
                    ):pl.name}
                  </span>
                  <span style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                    <span style={{fontSize:10,color:"var(--text-3)",background:"var(--bg)",padding:"1px 6px",borderRadius:10}}>{pl.category}</span>
                    <span style={{fontSize:12,fontWeight:600,color:"var(--green)"}}>{fmt(pl.price)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Template editor autocomplete dropdown */}
      {tmplAutocomplete && (() => {
        const sec = templateForm?.sections?.[tmplAutocomplete.si];
        const activeItem = sec?.items?.[tmplAutocomplete.ii];
        const suggestions = getSuggestions(activeItem?.name||"");
        if (!suggestions.length) return null;
        return (
          <div style={{
            position:"fixed",
            top: tmplAutocomplete.y,
            left: tmplAutocomplete.x,
            width: tmplAutocomplete.width,
            background:"var(--card)",
            border:"1px solid var(--border)",
            borderRadius:"var(--radius-sm)",
            boxShadow:"var(--shadow-lg)",
            zIndex:99999,
            maxHeight:220,
            overflowY:"auto",
          }}>
            {suggestions.map((pl,idx)=>{
              const query = (activeItem?.name||"").toLowerCase();
              const nameLower = pl.name.toLowerCase();
              const matchIdx = nameLower.indexOf(query);
              return (
                <div
                  key={pl.id||idx}
                  onMouseDown={e=>{e.preventDefault();selectTmplSuggestion(tmplAutocomplete.si,tmplAutocomplete.ii,pl);}}
                  style={{padding:"8px 12px",cursor:"pointer",fontSize:12.5,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,borderBottom:"1px solid var(--border)",background:"var(--card)"}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--bg)"}
                  onMouseLeave={e=>e.currentTarget.style.background="var(--card)"}
                >
                  <span style={{fontWeight:500,color:"var(--text)",flex:1}}>
                    {matchIdx>=0?(
                      <>
                        {pl.name.slice(0,matchIdx)}
                        <span style={{color:"var(--red)",fontWeight:700}}>{pl.name.slice(matchIdx,matchIdx+(activeItem?.name||"").length)}</span>
                        {pl.name.slice(matchIdx+(activeItem?.name||"").length)}
                      </>
                    ):pl.name}
                  </span>
                  <span style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                    <span style={{fontSize:10,color:"var(--text-3)",background:"var(--bg)",padding:"1px 6px",borderRadius:10}}>{pl.category}</span>
                    <span style={{fontSize:12,fontWeight:600,color:"var(--green)"}}>{fmt(pl.price)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        );
      })()}
      {guard&&<UnsavedChangesGuard t={t} onSave={async()=>{await guard.save();setGuard(null);setIsDirty(false);}} onDiscard={()=>{guard.close();setGuard(null);setIsDirty(false);}} onKeepEditing={()=>setGuard(null)}/>}
      {confirmDelete&&!confirmDelete._type&&<ConfirmDelete message={`Supprimer la soumission "${confirmDelete.title}"? Tous les éléments seront perdus.`} onConfirm={()=>{deleteEstimate(confirmDelete.id);setConfirmDelete(null);}} onCancel={()=>setConfirmDelete(null)}/>}
      {confirmDelete?._type==="template"&&<ConfirmDelete message={`Supprimer le modèle "${confirmDelete.name}"?`} onConfirm={()=>{deleteTemplate(confirmDelete.id);setConfirmDelete(null);}} onCancel={()=>setConfirmDelete(null)}/>}
      {confirmDelete?._type==="sectionTemplate"&&<ConfirmDelete message={`Supprimer le modèle "${confirmDelete.name}"?`} onConfirm={()=>{deleteSectionTemplate(confirmDelete.id);setConfirmDelete(null);}} onCancel={()=>setConfirmDelete(null)}/>}
      {innerConfirm&&<ConfirmDelete
        message={innerConfirm.type==="section"?`Supprimer la section "${innerConfirm.label}" et tous ses items?`:`Supprimer l'item "${innerConfirm.label}"?`}
        onConfirm={()=>{
          if(innerConfirm.type==="section") removeSection(innerConfirm.sid);
          else removeItem(innerConfirm.sid,innerConfirm.iid);
          setInnerConfirm(null);
        }}
        onCancel={()=>setInnerConfirm(null)}
      />}
      {plModal&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setPlModal(null)}>
          <div className="modal" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><Icon name="bookmark" size={15}/> Sauvegarder au prix / Save to Price List</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setPlModal(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              {plModal.dupWarning&&(
                <div style={{background:"#fef3c7",border:"1px solid #fcd34d",borderRadius:6,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#92400e"}}>
                  Un article avec ce nom existe déjà. / An item with this name already exists.
                  <div style={{display:"flex",gap:8,marginTop:10}}>
                    <button className="btn btn-primary btn-sm" onClick={savePlItem}>Écraser / Overwrite</button>
                    <button className="btn btn-secondary btn-sm" onClick={()=>setPlModal(p=>({...p,dupWarning:false,matchId:null}))}>Annuler / Cancel</button>
                  </div>
                </div>
              )}
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.priceList?.item||"Article"} *</label><input className="form-input" value={plModal.form.name||""} onChange={e=>setPlModal(p=>({...p,form:{...p.form,name:e.target.value},dupWarning:false,matchId:null}))}/></div>
                <div className="form-group"><label>{t.priceList?.category||"Catégorie"}</label><select className="form-select" value={plModal.form.category||"Material"} onChange={e=>setPlModal(p=>({...p,form:{...p.form,category:e.target.value}}))}>{["Material","Labor","Equipment","Tools","Subcontractor"].map(c=><option key={c}>{c}</option>)}</select></div>
                <div className="form-group"><label>{t.priceList?.unit||"Unité"}</label><input className="form-input" value={plModal.form.unit||""} onChange={e=>setPlModal(p=>({...p,form:{...p.form,unit:e.target.value}}))}/></div>
                <div className="form-group"><label>{t.priceList?.cost||"Coût"} ($)</label><input className="form-input" type="number" value={plModal.form.cost||""} onChange={e=>setPlModal(p=>({...p,form:{...p.form,cost:e.target.value}}))} onFocus={e=>e.target.select()}/></div>
                <div className="form-group"><label>{t.priceList?.price||"Prix"} ($)</label><input className="form-input" type="number" value={plModal.form.price||""} onChange={e=>setPlModal(p=>({...p,form:{...p.form,price:e.target.value}}))} onFocus={e=>e.target.select()}/></div>
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.priceList?.supplier||"Fournisseur"}</label>
                  <div className="autocomplete-wrap">
                    <input className="form-input" value={plModal.form.supplier||""} autoComplete="off"
                      onChange={e=>setPlModal(p=>({...p,form:{...p.form,supplier:e.target.value}}))}
                      onFocus={()=>setPlSupplierAC(true)}
                      onBlur={()=>setTimeout(()=>setPlSupplierAC(false),150)}
                    />
                    {plSupplierSuggestions.length>0&&(
                      <div className="autocomplete-dropdown">
                        {plSupplierSuggestions.map(s=>(
                          <div key={s} className="autocomplete-item" onMouseDown={e=>{e.preventDefault();setPlModal(p=>({...p,form:{...p.form,supplier:s}}));setPlSupplierAC(false);}}>
                            <span className="autocomplete-name">{s}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setPlModal(null)}>{t.estimates?.cancel||"Annuler"}</button>
              <button className="btn btn-primary" disabled={!plModal.form.name?.trim()} onClick={savePlItem}><Icon name="bookmark" size={14}/> {t.priceList?.save||"Sauvegarder"}</button>
            </div>
          </div>
        </div>
      )}
      {reportModalEst&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setReportModalEst(null)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-header">
              <div className="modal-title">{t.estimates.reportOptions}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setReportModalEst(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <div className="report-preset-cards">
                {[["full",t.estimates.reportFull],["client",t.estimates.reportClient]].map(([key,label])=>(
                  <div key={key} className={`report-preset-card${reportPreset===key?" active":""}`} onClick={()=>applyReportPreset(key)}>
                    {label}
                  </div>
                ))}
              </div>
              <div>
                {[
                  ["showLineItems",t.estimates.reportShowLineItems,false],
                  ["showUnitCost",t.estimates.reportShowUnitCost,true],
                  ["showUnitPrice",t.estimates.reportShowUnitPrice,true],
                  ["showLineItemTotals",t.estimates.reportShowLineItemTotals,true],
                  ["showSectionSubtotals",t.estimates.reportShowSectionSubtotals,false],
                  ["showTaxes",t.estimates.reportShowTaxes,false],
                  ["showReferenceMaterials",t.estimates.reportShowReferenceMaterials,false],
                  ["showDesignPlans",t.estimates.reportShowDesignPlans,false],
                ].map(([key,label,isChild])=>{
                  const disabled = isChild && !reportConfig.showLineItems;
                  return (
                    <div key={key} className="report-toggle-row" style={isChild?{paddingLeft:24}:{}}>
                      <input type="checkbox" id={`rc-${key}`} checked={!!reportConfig[key]} disabled={disabled} style={disabled?{opacity:0.4,pointerEvents:"none"}:{}} onChange={e=>{
                        if(key==="showLineItems"){
                          const v=e.target.checked;
                          setReportConfig(p=>({...p,showLineItems:v,showUnitCost:v?true:false,showUnitPrice:v?true:false,showLineItemTotals:v?true:false}));
                        } else {
                          setReportConfig(p=>({...p,[key]:e.target.checked}));
                        }
                        setReportPreset("custom");
                      }}/>
                      <label htmlFor={`rc-${key}`} style={disabled?{opacity:0.4,pointerEvents:"none"}:{}}>{label}</label>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setReportModalEst(null)}>{t.estimates.cancel}</button>
              <button className="btn btn-primary" onClick={doPrintEstimate}><Icon name="printer" size={14}/>{t.estimates.reportPrint}</button>
            </div>
          </div>
        </div>
      )}
      {jobBuilderEstimate&&(
        <JobBuilder
          t={t}
          estimate={jobBuilderEstimate}
          customer={customers.find(c=>c.id===jobBuilderEstimate.customerId)}
          customers={customers}
          teamMembers={teamMembers||[]}
          crews={crews||[]}
          scheduleNotes={scheduleNotes}
          updateScheduleNote={updateScheduleNote}
          onSave={async(job)=>{ const ref=await addJob(job); return ref; }}
          onClose={()=>setJobBuilderEstimate(null)}
        />
      )}
      {/* ── VERSION PREVIEW ── */}
      {versionPreview&&(
        <div className="modal-backdrop" style={{zIndex:300}} onClick={e=>e.target===e.currentTarget&&setVersionPreview(null)}>
          <div className="modal modal-lg">
            <div className="ver-preview-header">
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
                <div>
                  <div className="ver-preview-title">Version {versionPreview.versionNumber}{versionPreview.label?" — "+versionPreview.label:""}</div>
                  <div className="ver-preview-sub">{fmtVerDate(versionPreview.savedAt)}{versionPreview.savedBy?" · "+versionPreview.savedBy:""}</div>
                </div>
                <button className="btn-icon" title={t.common.close} onClick={()=>setVersionPreview(null)}><Icon name="x"/></button>
              </div>
            </div>
            <div className="modal-body">
              {(versionPreview.sections||[]).filter(s=>s.type!=="group").map((sec,si)=>{
                const secItems = (sec.items||[]).filter(i=>i.type!=="subheading");
                const secTotal = secItems.reduce((a,i)=>a+(i.unitPrice||0)*(i.qty||1),0);
                return (
                  <div key={sec.id||si} className="ver-preview-section">
                    <div className="ver-preview-section-header">{sec.title||"Section "+(si+1)}</div>
                    {(sec.items||[]).length>0&&(
                      <>
                        <div className="ver-preview-item ver-preview-item-head">
                          <span>Item</span><span>Qté</span><span>Unité</span><span style={{textAlign:"right"}}>Prix unit.</span><span style={{textAlign:"right"}}>Total</span>
                        </div>
                        {(sec.items||[]).map((it,ii)=>
                          it.type==="subheading"
                            ? <div key={it.id||ii} className="ver-preview-subheading">{it.label}</div>
                            : <div key={it.id||ii} className="ver-preview-item">
                                <span>{it.name}</span>
                                <span>{it.qty}</span>
                                <span>{it.unit}</span>
                                <span style={{textAlign:"right"}}>{fmt(it.unitPrice||0)}</span>
                                <span style={{textAlign:"right"}}>{fmt((it.unitPrice||0)*(it.qty||1))}</span>
                              </div>
                        )}
                      </>
                    )}
                    <div className="ver-preview-section-total">Sous-total section : {fmt(secTotal)}</div>
                  </div>
                );
              })}
              {(()=>{
                const sub = versionPreview.totalBeforeTax || 0;
                const tps = Math.round(sub*0.05*100)/100;
                const tvq = Math.round(sub*0.09975*100)/100;
                const total = versionPreview.totalWithTax || Math.round((sub+tps+tvq)*100)/100;
                return (
                  <div className="ver-preview-totals">
                    <div className="ver-preview-total-row"><span>Sous-total</span><span>{fmt(sub)}</span></div>
                    <div className="ver-preview-total-row"><span>TPS (5%)</span><span>{fmt(tps)}</span></div>
                    <div className="ver-preview-total-row"><span>TVQ (9.975%)</span><span>{fmt(tvq)}</span></div>
                    <div className="ver-preview-total-row"><span>Total</span><span>{fmt(total)}</span></div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setVersionPreview(null)}>{t.common.close}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── VERSION RESTORE CONFIRM ── */}
      {versionRestoreConfirm&&(
        <div className="modal-backdrop" style={{zIndex:300}} onClick={e=>e.target===e.currentTarget&&setVersionRestoreConfirm(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <div className="modal-title">{t.estimates.versionRestoreBtn} — Version {versionRestoreConfirm.versionNumber}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setVersionRestoreConfirm(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:14,color:"var(--text-2)",margin:0}}>{t.estimates.versionRestoreMsg}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setVersionRestoreConfirm(null)}>{t.estimates.cancel}</button>
              <button className="btn btn-primary" onClick={()=>doRestoreVersion(versionRestoreConfirm)}>{t.estimates.versionRestoreBtn}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── VERSION DELETE CONFIRM ── */}
      {versionDeleteConfirm&&(
        <ConfirmDelete
          message={`Supprimer cette version? / Delete this version?\n\nVersion ${versionDeleteConfirm.ver.versionNumber}${versionDeleteConfirm.ver.label?" — "+versionDeleteConfirm.ver.label:""}`}
          onConfirm={()=>doDeleteVersion(versionDeleteConfirm.ver)}
          onCancel={()=>setVersionDeleteConfirm(null)}
        />
      )}

      {/* ── VERSION DUPLICATE WARNING ── */}
      {versionDupWarning&&(
        <div className="modal-backdrop" style={{zIndex:300}} onClick={e=>e.target===e.currentTarget&&setVersionDupWarning(null)}>
          <div className="modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">⚠ Version similaire / Similar version</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setVersionDupWarning(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:14,color:"var(--text-2)",margin:0,lineHeight:1.6}}>
                Une version similaire existe déjà (<strong>{versionDupWarning.matchVer.label||"Sans étiquette"}</strong> — {fmtVerDate(versionDupWarning.matchVer.savedAt)}). Sauvegarder quand même?
                <br/><br/>
                A similar version already exists (<strong>{versionDupWarning.matchVer.label||"Unlabelled"}</strong> — {fmtVerDate(versionDupWarning.matchVer.savedAt)}). Save anyway?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setVersionDupWarning(null)}>Annuler / Cancel</button>
              <button className="btn btn-primary" onClick={()=>doSaveVersion(true)}>Sauvegarder quand même / Save anyway</button>
            </div>
          </div>
        </div>
      )}

      {commsEstimate&&(
        <div className="modal-backdrop" style={{zIndex:200}} onClick={e=>e.target===e.currentTarget&&setCommsEstimate(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}><Icon name="eye" size={16}/>Communications — {commsEstimate.title}</div>
                <div className="modal-sub">{comms.filter(c=>c.estimateId===commsEstimate.id).length} entrées · {commsEstimate.number}</div>
              </div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setCommsEstimate(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <CommunicationsTimeline comms={comms} customers={customers} estimates={estimates} jobs={jobs||[]} teamMembers={teamMembers||[]} onAdd={addComm} onEdit={updateComm} onDelete={deleteComm} contextEstimateId={commsEstimate.id} contextCustomerId={commsEstimate.customerId}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Estimates;
